import { KurooApiError, KurooClient } from "@kuroo/sdk";
import type { AvailableSlot, Booking, BookingAnalyticsEvent, BookingAnalyticsEventName, BookingAnalyticsKind, BookingAnalyticsReason, BookingCheckout, BookingExtraSelection, PaymentLinkStatusResponse, PriceQuote, PriceQuoteBreakdown, RecurringFrequency, RecurringOccurrence, RecurringRule, RecurringSeriesCheckout, RecurringSeriesPreview, ServicePaymentMode, ServiceSummary, SlotHoldResult } from "@kuroo/contracts";
import type { CatalogExtra, CatalogLocation, CatalogProvider } from "@kuroo/sdk";
import { BOOKING_ELEMENT_NAME } from "@kuroo/brand";
import { resolveWidgetTheme } from "@kuroo/ui/tokens";
import { escapeHtml, isAbortError, localDateValue, monthStart, monthValue, randomIdempotency, zonedDateTimeFields, zonedDateTimeToIso } from "./booking-utils";
import { bookingAnalyticsEvent, createBookingAnalyticsState, markBookingCancelled, markBookingConfirmed, markClientSelected, markServiceSelected, recordBookingSlotConflict } from "./booking-analytics";
import type { BookingAnalyticsDetails } from "./booking-analytics";
import { bookingSteps, renderBookingWidgetShell, stepTitles, type BookingStep } from "./booking-widget-view";
import { adjustmentReasonText, couponCodeFromForm, errorMessage, formatMoney, formatOccurrenceDateTime, formatSlot, oneOffEndAt, recurringCountFromForm, recurringParticipantsFromForm, recurringRuleFromForm, renderPriceTooltip, renderStaticPriceTooltip, selectedExtrasFromForm } from "./booking-widget-helpers";
import { renderCalendarCells, renderCalendarMonthOptions, renderCalendarYearOptions } from "./booking-widget-calendar";
import { renderBookingSlotsMarkup } from "./booking-widget-slots";
import { renderBookingPaymentMarkup, renderRecurringPaymentMarkup } from "./booking-widget-payment";
import { renderRecurringPreview } from "./booking-widget-recurring";
export { KurooConciergeElement, defineKurooConcierge } from "./concierge";

declare global {
  interface Window {
    KurooCrmConfig?: { apiBaseUrl?: string };
  }
}

const elementName = BOOKING_ELEMENT_NAME;

type PendingHold = SlotHoldResult & { startAt: string; endAt: string; providerId?: string; locationId?: string };

export class KurooBookingElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["api-base-url", "organization-id", "service-id", "provider-id", "location-id", "locale", "theme", "waitlist-enabled"];
  }

  private readonly root = this.attachShadow({ mode: "open" });
  private client?: KurooClient;
  private pendingHold?: PendingHold;
  private selectedSlot?: AvailableSlot;
  private recurringPreview?: RecurringSeriesPreview;
  private recurringCheckout?: RecurringSeriesCheckout;
  private bookingCheckout?: BookingCheckout;
  private bookingPaymentStatus?: PaymentLinkStatusResponse;
  private selectedDate = localDateValue();
  private calendarMonth = monthValue(new Date());
  private currentStep: BookingStep = "datetime";
  private lastBooking?: Booking;
  private catalogAbort?: AbortController;
  private slotsAbort?: AbortController;
  private calendarAbort?: AbortController;
  private recurringPreviewAbort?: AbortController;
  private serviceExtrasAbort?: AbortController;
  private catalogRequestSequence = 0;
  private requestSequence = 0;
  private calendarRequestSequence = 0;
  private recurringPreviewRequestSequence = 0;
  private serviceExtrasRequestSequence = 0;
  private countdownTimer?: number;
  private paymentPollTimer?: number;
  private recurringPreviewDebounceTimer?: number;
  private reviewQuoteAbort?: AbortController;
  private pricePulseTimer?: number;
  private paymentPollAttempts = 0;
  private layoutResizeObserver?: ResizeObserver;
  private layoutResizeFrame?: number;
  private layoutResizePendingWidth?: number;
  private documentThemeObserver?: MutationObserver;
  /** Prevents the manual action and the background poller from issuing
   * overlapping status requests. The old flow silently re-rendered the same
   * pending card, which made the button look inert when Mercado Pago had not
   * delivered its webhook yet. */
  private paymentRefreshing = false;
  private availabilityLoading = false;
  private recurringPricingLoading = false;
  private oneOffQuoteLoading = false;
  private reviewRefreshPending = false;
  private oneOffQuote?: PriceQuote;
  private oneOffQuoteRequestSequence = 0;
  private services: ServiceSummary[] = [];
  private providers: CatalogProvider[] = [];
  private locations: CatalogLocation[] = [];
  private extras: CatalogExtra[] = [];
  private availabilityDays = new Map<string, number>();
  private weeklyDaysCustomized = false;
  private analytics = createBookingAnalyticsState();

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.isConnected || oldValue === newValue) return;
    if (name === "api-base-url" || name === "organization-id") this.client = new KurooClient({ baseUrl: this.getAttribute("api-base-url") ?? window.KurooCrmConfig?.apiBaseUrl ?? window.location.origin, organizationId: this.getAttribute("organization-id") ?? undefined });
    if (name === "service-id") this.currentStep = "datetime";
    if (["api-base-url", "organization-id", "service-id", "provider-id", "location-id", "locale", "theme", "waitlist-enabled"].includes(name)) {
      this.cancelRecurringPreviewRequest({ clearPreview: true });
      this.render();
      void this.loadCatalogAndSlots();
    }
  }

  connectedCallback(): void {
    this.beginAnalyticsFlow();
    this.currentStep = "datetime";
    this.selectedDate = localDateValue();
    this.calendarMonth = monthValue(new Date());
    this.client = new KurooClient({ baseUrl: this.getAttribute("api-base-url") ?? window.KurooCrmConfig?.apiBaseUrl ?? window.location.origin, organizationId: this.getAttribute("organization-id") ?? undefined });
    this.observeDocumentTheme();
    this.render();
    this.emitBookingAnalytics("booking_started", { step: "datetime", actor: "anonymous" });
    this.observeResponsiveLayout();
    void this.loadCatalogAndSlots();
  }

  disconnectedCallback(): void {
    this.catalogAbort?.abort();
    this.slotsAbort?.abort();
    this.calendarAbort?.abort();
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    this.serviceExtrasAbort?.abort();
    this.reviewQuoteAbort?.abort();
    this.layoutResizeObserver?.disconnect();
    this.layoutResizeObserver = undefined;
    this.documentThemeObserver?.disconnect();
    this.documentThemeObserver = undefined;
    if (this.layoutResizeFrame !== undefined) window.cancelAnimationFrame(this.layoutResizeFrame);
    this.layoutResizeFrame = undefined;
    this.layoutResizePendingWidth = undefined;
    if (this.countdownTimer) window.clearInterval(this.countdownTimer);
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    void this.releasePendingHold();
  }

  private observeDocumentTheme(): void {
    this.documentThemeObserver?.disconnect();
    this.documentThemeObserver = undefined;
    const configuredTheme = () => this.getAttribute("theme");
    const sync = (): void => {
      const theme = configuredTheme();
      if (theme === "dark" || theme === "light") return;
      const resolved = resolveWidgetTheme(theme);
      if (this.dataset.theme !== resolved) this.dataset.theme = resolved;
    };
    sync();
    if (typeof MutationObserver === "undefined" || typeof document === "undefined") return;
    this.documentThemeObserver = new MutationObserver(sync);
    this.documentThemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  }

  private beginAnalyticsFlow(): void {
    this.analytics = createBookingAnalyticsState();
  }

  private waitlistEnabled(): boolean {
    const value = this.getAttribute("waitlist-enabled");
    return value !== null && value !== "false";
  }

  private emitBookingAnalytics(name: BookingAnalyticsEventName, details: BookingAnalyticsDetails = {}): void {
    const detail = bookingAnalyticsEvent(this.analytics, name, this.currentStep, this.serviceIdFromForm(), details);
    this.dispatchEvent(new CustomEvent<BookingAnalyticsEvent>("kuroo:booking-analytics", { bubbles: true, composed: true, detail }));
  }

  private emitClientSelected(): void {
    const next = markClientSelected(this.analytics);
    if (!next) return;
    this.analytics = next;
    this.emitBookingAnalytics("client_selected", { actor: "customer", step: "information" });
  }

  private emitServiceSelected(serviceId: string): void {
    const next = markServiceSelected(this.analytics, serviceId);
    if (!next) return;
    this.analytics = next;
    this.emitBookingAnalytics("service_selected", { actor: "anonymous", step: "datetime", serviceId });
  }

  private emitBookingConfirmed(kind: BookingAnalyticsKind): void {
    const next = markBookingConfirmed(this.analytics);
    if (!next) return;
    this.analytics = next;
    this.emitBookingAnalytics("booking_confirmed", {
      actor: "customer",
      step: "confirmation",
      kind,
      durationMs: this.analytics.startedAt ? Date.now() - this.analytics.startedAt : undefined
    });
  }

  private emitBookingCancelled(reason: BookingAnalyticsReason): void {
    const next = markBookingCancelled(this.analytics);
    if (!next) return;
    this.analytics = next;
    this.emitBookingAnalytics("booking_cancelled", { actor: "customer", reason });
  }

  private emitSlotConflict(error: unknown): void {
    const next = recordBookingSlotConflict(this.analytics, error);
    if (!next) return;
    this.analytics = next;
    this.emitBookingAnalytics("slot_conflict", { reason: "availability_conflict", date: this.selectedDate });
  }

  private observeResponsiveLayout(): void {
    this.layoutResizeObserver?.disconnect();
    this.layoutResizeObserver = undefined;
    if (this.layoutResizeFrame !== undefined) window.cancelAnimationFrame(this.layoutResizeFrame);
    this.layoutResizeFrame = undefined;
    this.layoutResizePendingWidth = undefined;
    if (typeof ResizeObserver === "undefined") return;

    const updateLayout = (width: number): void => {
      if (!Number.isFinite(width) || width <= 0) return;
      const layout = width <= 480 ? "narrow" : width <= 720 ? "compact" : "wide";
      if (this.dataset.layout !== layout) this.dataset.layout = layout;
    };

    updateLayout(this.getBoundingClientRect().width);
    const scheduleLayoutUpdate = (width: number): void => {
      if (!Number.isFinite(width) || width <= 0) return;
      this.layoutResizePendingWidth = width;
      if (this.layoutResizeFrame !== undefined) return;
      this.layoutResizeFrame = window.requestAnimationFrame(() => {
        this.layoutResizeFrame = undefined;
        const pendingWidth = this.layoutResizePendingWidth;
        this.layoutResizePendingWidth = undefined;
        if (pendingWidth !== undefined) updateLayout(pendingWidth);
      });
    };
    this.layoutResizeObserver = new ResizeObserver((entries) => {
      scheduleLayoutUpdate(entries[0]?.contentRect.width ?? this.getBoundingClientRect().width);
    });
    this.layoutResizeObserver.observe(this);
  }

  private render(): void {
    const serviceId = this.getAttribute("service-id") ?? "";
    const theme = resolveWidgetTheme(this.getAttribute("theme"));
    const accent = theme === "dark" ? "var(--ui-accent)" : "var(--ui-primary)";
    this.dataset.theme = theme;
    const defaultMonth = monthStart(this.calendarMonth);
    const monthLabel = new Intl.DateTimeFormat(this.getAttribute("locale") ?? "es-ES", { month: "long", year: "numeric" }).format(defaultMonth);
    const weekdayLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
    this.root.innerHTML = renderBookingWidgetShell({
      serviceId,
      accent,
      defaultMonth,
      monthLabel,
      weekdayLabels,
      selectedDate: this.selectedDate,
      currentStep: this.currentStep
    });
    this.bindForm();
    this.renderCalendar();
    this.updateStepUI();
  }

  private bindForm(): void {
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    if (!form) return;
    const dateInput = form.elements.namedItem("date") as HTMLInputElement;
    dateInput.value = this.selectedDate;
    const serviceSelect = form.elements.namedItem("serviceId") as HTMLSelectElement;
    serviceSelect?.addEventListener("change", () => {
      this.weeklyDaysCustomized = false;
      void this.handleServiceChange(form, serviceSelect.value);
    });
    for (const name of ["providerId", "locationId"]) {
      const control = form.elements.namedItem(name) as HTMLSelectElement;
      control?.addEventListener("change", () => {
        if (name === "providerId") {
          const locationSelect = form.elements.namedItem("locationId") as HTMLSelectElement | null;
          if (locationSelect && !this.getAttribute("location-id")) locationSelect.value = "";
          void this.loadCatalogAndSlots();
          return;
        }
        void this.loadSlots();
      });
    }
    const recurrenceToggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    recurrenceToggle?.addEventListener("change", () => {
      this.cancelRecurringPreviewRequest({ clearPreview: true });
      this.renderExtras(form);
      this.updateStepUI();
    });
    form.querySelectorAll<HTMLInputElement>('input[name="weeklyDay"]').forEach((input) => input.addEventListener("change", () => { this.weeklyDaysCustomized = true; this.scheduleRecurringPreviewRefresh(form); }));
    this.root.querySelector('[data-action="add-recurring-participant"]')?.addEventListener("click", () => this.addRecurringParticipant(form));
    form.querySelectorAll<HTMLInputElement>('[data-recurring-participant] input').forEach((input) => input.addEventListener("input", () => this.scheduleRecurringPreviewRefresh(form)));
    const recurrenceFrequency = form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement | null;
    recurrenceFrequency?.addEventListener("change", () => { this.updateRecurrenceFields(form); this.scheduleRecurringPreviewRefresh(form); });
    const monthlyMode = form.elements.namedItem("monthlyMode") as HTMLSelectElement | null;
    monthlyMode?.addEventListener("change", () => { this.updateRecurrenceFields(form); this.scheduleRecurringPreviewRefresh(form); });
    for (const name of ["recurrenceInterval", "recurrenceCount", "monthlyDay", "monthlyWeekday", "monthlyWeek", "persons"]) {
      const control = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
      control?.addEventListener("input", () => this.scheduleRecurringPreviewRefresh(form));
      control?.addEventListener("change", () => this.scheduleRecurringPreviewRefresh(form));
    }
    const personsControl = form.elements.namedItem("persons") as HTMLInputElement | null;
    personsControl?.addEventListener("input", () => {
      this.oneOffQuote = undefined;
      if (!recurrenceToggle?.checked && this.currentStep === "payment") this.scheduleReviewQuoteRefresh(form);
    });
    personsControl?.addEventListener("change", () => {
      this.oneOffQuote = undefined;
      if (!recurrenceToggle?.checked && this.currentStep === "payment") this.scheduleReviewQuoteRefresh(form);
    });
    form.addEventListener("input", (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("[data-review-coupon]")) {
        this.scheduleReviewQuoteRefresh(form);
      }
    });
    form.addEventListener("change", (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("[data-review-coupon]")) this.scheduleReviewQuoteRefresh(form);
    });
    form.querySelectorAll<HTMLInputElement>('input[name^="recurring-extra-"]').forEach((input) => input.addEventListener("change", () => this.scheduleRecurringPreviewRefresh(form)));
    this.root.querySelector('[data-action="prev-month"]')?.addEventListener("click", () => this.changeMonth(-1));
    this.root.querySelector('[data-action="next-month"]')?.addEventListener("click", () => this.changeMonth(1));
    this.root.querySelector('[data-action="next-date"]')?.addEventListener("click", () => void this.findNextDate());
    this.root.querySelector('[data-action="recurrence-decrease"]')?.addEventListener("click", () => this.changeRecurrenceCount(form, -1));
    this.root.querySelector('[data-action="recurrence-increase"]')?.addEventListener("click", () => this.changeRecurrenceCount(form, 1));
    this.root.querySelector('[data-retry-catalog]')?.addEventListener("click", () => {
      const retry = this.root.querySelector("[data-catalog-retry]") as HTMLElement | null;
      if (retry) retry.hidden = true;
      void this.loadCatalogAndSlots();
    });
    this.root.querySelector('[data-action="reset"]')?.addEventListener("click", () => { void this.resetBooking(); });
    this.root.querySelector('[data-action="back"]')?.addEventListener("click", () => void this.goPrevious());
    this.root.querySelector('[data-action="previous"]')?.addEventListener("click", () => void this.goPrevious());
    this.root.querySelector('[data-action="collapse"]')?.addEventListener("click", () => {
      const shell = this.root.querySelector(".booking-shell");
      const label = this.root.querySelector(".collapse-label");
      shell?.classList.toggle("collapsed");
      if (label) label.textContent = shell?.classList.contains("collapsed") ? "Expandir menú" : "Contraer menú";
    });
    this.root.querySelectorAll("[data-step-target]").forEach((node) => node.addEventListener("click", () => {
      const target = (node as HTMLElement).dataset.stepTarget as BookingStep | undefined;
      if (target) void this.goToStep(target);
    }));
    this.updateRecurrenceAvailability(form);
    this.updateRecurrenceFields(form);
    form.addEventListener("submit", (event) => void this.submit(event as SubmitEvent));
  }

  private updateStepUI(): void {
    const currentIndex = bookingSteps.findIndex((step) => step.key === this.currentStep);
    this.root.querySelectorAll<HTMLElement>("[data-step-panel]").forEach((panel) => { panel.hidden = panel.dataset.stepPanel !== this.currentStep; });
    this.root.querySelectorAll<HTMLElement>("[data-step]").forEach((step) => {
      const index = bookingSteps.findIndex((item) => item.key === step.dataset.step);
      step.classList.toggle("active", index === currentIndex);
      step.classList.toggle("complete", index >= 0 && index < currentIndex || this.currentStep === "confirmation");
      const marker = step.querySelector("[data-step-index]");
      if (marker) marker.textContent = index >= 0 && index < currentIndex || this.currentStep === "confirmation" ? "✓" : String(index + 1);
      const button = step.querySelector("button");
      if (button) button.disabled = !(index >= 0 && index <= currentIndex) || this.currentStep === "confirmation";
    });
    const title = this.root.querySelector("[data-step-title]");
    if (title) title.textContent = stepTitles[this.currentStep];
    const submit = this.root.querySelector('[data-action="continue"]') as HTMLButtonElement | null;
    if (submit) {
      submit.textContent = this.continueLabel();
      submit.disabled = this.continueDisabled();
    }
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    const busy = this.availabilityLoading || this.recurringPricingLoading || this.oneOffQuoteLoading || this.paymentRefreshing || this.reviewRefreshPending;
    if (form) form.setAttribute("aria-busy", String(busy));
    const body = this.root.querySelector(".booking-body") as HTMLElement | null;
    if (body) body.setAttribute("aria-busy", String(busy));
    const back = this.root.querySelector('[data-action="back"]') as HTMLButtonElement | null;
    const previous = this.root.querySelector('[data-action="previous"]') as HTMLButtonElement | null;
    const canGoBack = this.currentStep !== "datetime" && this.currentStep !== "confirmation";
    if (back) back.disabled = !canGoBack;
    if (previous) previous.hidden = !canGoBack;
    if (this.currentStep === "information") {
      const customer = this.root.querySelector("[data-customer]") as HTMLElement | null;
      const empty = this.root.querySelector("[data-customer-empty]") as HTMLElement | null;
      if (customer) customer.hidden = !this.selectedSlot && !this.pendingHold && !this.recurringPreview && !this.bookingCheckout;
      if (empty) empty.hidden = Boolean(this.selectedSlot || this.pendingHold || this.recurringPreview || this.bookingCheckout);
    }
    this.renderRecurringInformationSummary();
    if (this.currentStep === "recurrence") this.renderRecurringPreview();
    if (this.currentStep === "payment") this.renderPayment();
    if (this.currentStep === "confirmation") this.renderConfirmation();
  }

  private continueLabel(): string {
    if (this.currentStep === "confirmation") return this.lastBooking ? "Reserva confirmada" : "Nueva reserva";
    if (this.currentStep === "information" && this.pendingHold) return "Confirmar reserva";
    if (this.currentStep === "information" && this.recurringPreview) return "Ir a revisión y pago";
    if (this.currentStep === "information" && !this.pendingHold) return "Ir a revisión y pago";
    if (this.currentStep === "payment" && !this.recurringCheckout && !this.bookingCheckout) return this.recurringPreview ? "Confirmar y crear checkout" : "Confirmar reserva";
    if (this.currentStep === "payment") return "Ver estado del pago";
    if (this.currentStep === "recurrence") return this.recurringPreview ? "Continuar con estas fechas" : "Previsualizar fechas";
    if (this.currentStep === "datetime" && !this.serviceIdFromForm()) return "Seleccionar servicio";
    if (this.currentStep === "datetime" && !this.selectedSlot) return "Comprobar disponibilidad";
    return "Continuar";
  }

  private continueDisabled(): boolean {
    if (this.availabilityLoading || this.recurringPricingLoading || this.oneOffQuoteLoading || this.paymentRefreshing || this.reviewRefreshPending) return true;
    if (this.currentStep === "confirmation") return Boolean(this.lastBooking);
    if (this.currentStep === "datetime") return !this.serviceIdFromForm() || !this.selectedSlot;
    if (this.currentStep === "recurrence") {
      if (!this.selectedSlot) return true;
      if (!this.recurringPreview) return false;
      const selections = this.selectedRecurringOccurrences();
      return !selections.some((occurrence) => occurrence.include) || this.recurringPreview.occurrences.some((occurrence) => occurrence.status === "unavailable" && selections.some((selection) => selection.sequence === occurrence.sequence && selection.include)) || this.recurringPreview.occurrences.some((occurrence) => occurrence.adjusted && selections.some((selection) => selection.sequence === occurrence.sequence && selection.include && !selection.adjustmentAccepted));
    }
    if (this.currentStep === "information") return !this.selectedSlot || (Boolean(this.recurringPreview) && !this.selectedRecurringOccurrences().some((occurrence) => occurrence.include));
    if (this.currentStep === "payment") return !this.recurringCheckout && !this.bookingCheckout && !this.selectedSlot;
    return false;
  }

  private serviceIdFromForm(): string {
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    return this.getAttribute("service-id") ?? (form?.elements.namedItem("serviceId") as HTMLSelectElement | null)?.value ?? "";
  }

  private serviceRecurrence(): ServiceSummary["recurrence"] {
    return this.services.find((service) => service.id === this.serviceIdFromForm())?.recurrence;
  }

  private servicePaymentMode(): ServicePaymentMode {
    const service = this.services.find((item) => item.id === this.serviceIdFromForm());
    return service?.paymentMode ?? service?.recurrence?.paymentMode ?? "on_site";
  }

  private updateRecurrenceAvailability(form: HTMLFormElement): void {
    const toggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    const note = form.querySelector("[data-recurrence-availability]") as HTMLElement | null;
    const disabledCard = form.querySelector("[data-recurrence-disabled-card]") as HTMLElement | null;
    const settings = this.serviceRecurrence();
    if (!toggle) return;
    toggle.disabled = !settings?.enabled;
    if (!settings?.enabled) {
      toggle.checked = false;
      this.recurringPreview = undefined;
      if (note) note.textContent = "Este servicio no tiene recurrencias habilitadas.";
      if (disabledCard) disabledCard.hidden = false;
      return;
    }
    if (disabledCard) disabledCard.hidden = true;
    if (note) {
      const allowedFrequencies = [["daily", settings.allowDaily, "diaria"], ["weekly", settings.allowWeekly, "semanal"], ["monthly", settings.allowMonthly, "mensual"]].filter(([, enabled]) => enabled).map(([, , label]) => label).join(", ");
      const cancellationWindow = settings.cancellationWindowMinutes / 60 >= 24 ? String(Math.round(settings.cancellationWindowMinutes / 1440)) + " día(s)" : String(settings.cancellationWindowMinutes) + " min";
      note.textContent = (allowedFrequencies || "Ninguna") + " · " + cancellationWindow + " para cancelar.";
    }
    const frequency = form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement | null;
    if (frequency) {
      const allowed: Record<string, boolean> = { daily: settings.allowDaily, weekly: settings.allowWeekly, monthly: settings.allowMonthly };
      [...frequency.options].forEach((option) => { option.disabled = !allowed[option.value]; });
      if (frequency.selectedOptions[0]?.disabled) {
        const first = [...frequency.options].find((option) => !option.disabled);
        if (first) frequency.value = first.value;
      }
    }
  }

  private updateRecurrenceFields(form: HTMLFormElement): void {
    const frequency = (form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement | null)?.value;
    const weekly = form.querySelector("[data-weekly-fields]") as HTMLElement | null;
    const monthly = form.querySelector("[data-monthly-fields]") as HTMLElement | null;
    if (weekly) weekly.hidden = frequency !== "weekly";
    if (monthly) monthly.hidden = frequency !== "monthly";
    const mode = (form.elements.namedItem("monthlyMode") as HTMLSelectElement | null)?.value;
    const dayField = form.querySelector("[data-monthly-day-field]") as HTMLElement | null;
    const positionFields = form.querySelector("[data-monthly-position-fields]") as HTMLElement | null;
    if (dayField) dayField.hidden = mode !== "day_of_month";
    if (positionFields) positionFields.hidden = mode !== "weekday_position";
  }

  private recurringRuleFromForm(form: HTMLFormElement): RecurringRule {
    return recurringRuleFromForm(form);
  }

  private selectedExtrasFromForm(form: HTMLFormElement, recurring = false): BookingExtraSelection[] {
    return selectedExtrasFromForm(form, this.extras, recurring);
  }

  private renderRecurrenceExtras(form: HTMLFormElement): void {
    const wrapper = form.querySelector("[data-recurring-extras]") as HTMLElement | null;
    const list = form.querySelector("[data-recurring-extra-list]") as HTMLElement | null;
    if (!wrapper || !list) return;
    const recurrenceToggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    list.innerHTML = "";
    if (!this.extras.length || !recurrenceToggle?.checked) { wrapper.hidden = true; return; }
    wrapper.hidden = false;
    const service = this.services.find((item) => item.id === this.serviceIdFromForm());
    list.innerHTML = this.extras.map((extra) => `<label class="extra-option"><input type="checkbox" name="recurring-extra-${escapeHtml(extra.id)}" value="${escapeHtml(extra.id)}" /> <span>${escapeHtml(extra.name)} <small>+${this.renderStaticPriceTooltip(extra.price_cents, service?.currency ?? "EUR", "Extra aplicado a cada ocurrencia de la serie.", "Precio del extra")}</small></span></label>`).join("");
    this.bindPriceTooltips(list);
    list.querySelectorAll<HTMLInputElement>('input[name^="recurring-extra-"]').forEach((input) => input.addEventListener("change", () => {
      const form = this.root.querySelector("form") as HTMLFormElement | null;
      if (form) this.scheduleRecurringPreviewRefresh(form);
    }));
  }

  private addRecurringParticipant(form: HTMLFormElement): void {
    const list = form.querySelector("[data-recurring-participant-list]") as HTMLElement | null;
    if (!list) return;
    const row = document.createElement("div");
    row.className = "participant-row";
    row.dataset.recurringParticipant = "true";
    row.innerHTML = '<input name="recurringParticipantName" placeholder="Nombre" aria-label="Nombre del participante" /><input name="recurringParticipantEmail" type="email" placeholder="Email (opcional)" aria-label="Email del participante" /><button type="button" class="icon-button" aria-label="Quitar participante">×</button>';
    row.querySelector("button")?.addEventListener("click", () => row.remove());
    list.append(row);
    row.querySelectorAll<HTMLInputElement>("input").forEach((input) => input.addEventListener("input", () => this.scheduleRecurringPreviewRefresh(form)));
  }

  private recurringParticipantsFromForm(form: HTMLFormElement): Array<{ name: string; email?: string }> {
    return recurringParticipantsFromForm(form);
  }

  private renderRecurringPreview(): void {
    renderRecurringPreview({
      root: this.root,
      preview: this.recurringPreview,
      recurringPricingLoading: this.recurringPricingLoading,
      providers: this.providers,
      locations: this.locations,
      formatOccurrenceDateTime: (value, timezone) => this.formatOccurrenceDateTime(value, timezone),
      adjustmentReasonText: (reason) => this.adjustmentReasonText(reason),
      renderPriceTooltip: (amountCents, currency, breakdown, label) => this.renderPriceTooltip(amountCents, currency, breakdown, label),
      bindPriceTooltips: (scope) => this.bindPriceTooltips(scope),
      changeRecurrenceCount: (form, delta) => this.changeRecurrenceCount(form, delta),
      scheduleRecurringPreviewRefresh: (form) => this.scheduleRecurringPreviewRefresh(form),
      updateRecurringSelectionSummary: () => this.updateRecurringSelectionSummary(),
      updateStepUI: () => this.updateStepUI(),
      validateRecurringOccurrenceInputs: () => this.validateRecurringOccurrenceInputs()
    });
  }

  private renderRecurringInformationSummary(): void {
    const container = this.root.querySelector("[data-recurring-information-summary]") as HTMLElement | null;
    const preview = this.recurringPreview;
    if (!container || !preview || this.currentStep !== "information") {
      if (container) { container.hidden = true; container.innerHTML = ""; }
      return;
    }
    const selected = this.selectedRecurringOccurrences().filter((occurrence) => occurrence.include);
    const selectedRows = preview.occurrences.filter((occurrence) => selected.some((item) => item.sequence === occurrence.sequence));
    const locale = this.getAttribute("locale") ?? "es-ES";
    const dates = selectedRows.map((occurrence) => {
      const date = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone: preview.timezone }).format(new Date(occurrence.startAt));
      const totalCents = occurrence.totalCents ?? occurrence.pricing?.totalCents ?? occurrence.amountCents + occurrence.taxCents;
      return `<li>${escapeHtml(date)} · ${this.renderPriceTooltip(totalCents, occurrence.currency, occurrence.pricing ?? null, `Ocurrencia ${occurrence.sequence + 1}`)}</li>`;
    }).join("");
    container.hidden = false;
    const total = this.recurringPricingLoading ? null : preview.totalCents;
    container.innerHTML = `<header><strong>Serie de ${selectedRows.length} fecha${selectedRows.length === 1 ? "" : "s"}</strong><span>${total === null ? '<span class="spinner" aria-hidden="true"></span> Recalculando…' : this.renderPriceTooltip(total, preview.currency, preview.pricing ?? null, "Total de la serie")}</span></header><ul>${dates || "<li>Elegí al menos una fecha.</li>"}</ul><p class="review-note">El importe de cada ocurrencia viene de la API y puede incluir reglas dinámicas, extras, cupón e impuestos.</p><button type="button" data-action="edit-recurring-dates">Editar fechas, profesional o sede</button>`;
    this.bindPriceTooltips(container);
    container.querySelector('[data-action="edit-recurring-dates"]')?.addEventListener("click", () => { this.currentStep = "recurrence"; this.updateStepUI(); });
  }

  private updateRecurringSelectionSummary(): void {
    const preview = this.recurringPreview;
    const container = this.root.querySelector("[data-recurring-preview]") as HTMLElement | null;
    if (!preview || !container) return;
    const rows = [...container.querySelectorAll<HTMLElement>("[data-occurrence-row]")];
    const included = rows.filter((row) => row.querySelector<HTMLInputElement>("[data-occurrence-include]")?.checked);
    const summary = container.querySelector("[data-recurring-selection-summary]");
    const totalNode = container.querySelector("[data-recurring-total]");
    if (summary) summary.textContent = `${included.length} fecha${included.length === 1 ? "" : "s"} seleccionada${included.length === 1 ? "" : "s"}`;
    if (totalNode) totalNode.innerHTML = this.recurringPricingLoading ? '<span class="spinner" aria-hidden="true"></span> Recalculando…' : this.renderPriceTooltip(preview.totalCents, preview.currency, preview.pricing ?? null, "Total de la serie");
    if (totalNode && !this.recurringPricingLoading) this.bindPriceTooltips(totalNode);
  }

  private selectedRecurringOccurrences(): Array<{ sequence: number; include: boolean; quoteId?: string | null; requestedStartAt?: string; startAt: string; endAt: string; providerId?: string | null; locationId?: string | null; adjustmentAccepted?: boolean }> {
    const preview = this.recurringPreview;
    const container = this.root.querySelector("[data-recurring-preview]") as HTMLElement | null;
    if (!preview || !container) return [];
    return preview.occurrences.map((occurrence) => {
      const row = container.querySelector<HTMLElement>(`[data-occurrence-row="${occurrence.sequence}"]`);
      const requestedStartAt = zonedDateTimeToIso(
        row?.querySelector<HTMLInputElement>("[data-occurrence-date]")?.value ?? "",
        row?.querySelector<HTMLInputElement>("[data-occurrence-time]")?.value ?? "",
        preview.timezone
      ) ?? occurrence.requestedStartAt;
      return {
        sequence: occurrence.sequence,
        include: row?.querySelector<HTMLInputElement>("[data-occurrence-include]")?.checked ?? false,
        quoteId: row?.dataset.occurrenceQuoteId || occurrence.quoteId || null,
        requestedStartAt,
        startAt: occurrence.startAt,
        endAt: occurrence.endAt,
        providerId: row?.querySelector<HTMLSelectElement>("[data-occurrence-provider]")?.value || null,
        locationId: row?.querySelector<HTMLSelectElement>("[data-occurrence-location]")?.value || null,
        adjustmentAccepted: !occurrence.adjusted || Boolean(row?.querySelector<HTMLInputElement>("[data-occurrence-adjustment]")?.checked)
      };
    });
  }

  private validateRecurringOccurrenceInputs(): boolean {
    const preview = this.recurringPreview;
    const container = this.root.querySelector("[data-recurring-preview]") as HTMLElement | null;
    if (!preview || !container) return true;
    let valid = true;
    container.querySelectorAll<HTMLElement>("[data-occurrence-row]").forEach((row) => {
      const date = row.querySelector<HTMLInputElement>("[data-occurrence-date]");
      const time = row.querySelector<HTMLInputElement>("[data-occurrence-time]");
      const requestedStartAt = zonedDateTimeToIso(date?.value ?? "", time?.value ?? "", preview.timezone);
      if (!requestedStartAt) {
        valid = false;
        time?.setCustomValidity("Elegí una fecha y hora válidas para esta ocurrencia.");
      } else {
        time?.setCustomValidity("");
      }
    });
    if (!valid) this.setStatus("Revisá la fecha y hora de cada ocurrencia antes de continuar.", true);
    return valid;
  }

  private recurringCountFromForm(form: HTMLFormElement, fallback: number): number {
    return recurringCountFromForm(form, fallback);
  }

  private changeRecurrenceCount(form: HTMLFormElement, delta: number): void {
    const input = form.elements.namedItem("recurrenceCount") as HTMLInputElement | null;
    if (!input) return;
    const current = this.recurringCountFromForm(form, 4);
    const next = Math.min(52, Math.max(1, current + Math.trunc(delta)));
    if (next === current) return;
    input.value = String(next);
    this.scheduleRecurringPreviewRefresh(form);
  }

  private couponCodeFromForm(form: HTMLFormElement): string | undefined {
    return couponCodeFromForm(form);
  }

  private oneOffEndAt(form: HTMLFormElement, slot: AvailableSlot, service: ServiceSummary): string {
    return oneOffEndAt(form, slot, service, this.extras);
  }

  private renderStaticPriceTooltip(amountCents: number, currency: string, description: string, label = "Detalle del precio"): string {
    return renderStaticPriceTooltip(amountCents, currency, description, label, this.getAttribute("locale") ?? "es-AR");
  }

  private renderPriceTooltip(amountCents: number, currency: string, breakdown?: PriceQuoteBreakdown | null, label = "Detalle del precio", extraDescription?: string): string {
    return renderPriceTooltip(amountCents, currency, breakdown, label, extraDescription, this.getAttribute("locale") ?? "es-AR");
  }

  private bindPriceTooltips(scope: ParentNode = this.root): void {
    scope.querySelectorAll<HTMLElement>("[data-price-tooltip]").forEach((node) => {
      if (node.dataset.tooltipBound === "true") return;
      node.dataset.tooltipBound = "true";
      const close = (event?: Event) => {
        if (event && node.contains(event.target as Node)) return;
        node.classList.remove("open");
        node.setAttribute("aria-expanded", "false");
      };
      node.addEventListener("click", () => {
        const open = !node.classList.contains("open");
        node.classList.toggle("open", open);
        node.setAttribute("aria-expanded", String(open));
      });
      node.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          node.click();
        }
        if (event.key === "Escape") close();
      });
      node.addEventListener("blur", () => window.setTimeout(() => close(), 0));
    });
  }

  private highlightPrices(): void {
    this.root.querySelectorAll<HTMLElement>(".price-with-tooltip").forEach((node) => node.classList.add("price-updated"));
    if (this.pricePulseTimer) window.clearTimeout(this.pricePulseTimer);
    this.pricePulseTimer = window.setTimeout(() => {
      this.root.querySelectorAll<HTMLElement>(".price-with-tooltip").forEach((node) => node.classList.remove("price-updated"));
      this.pricePulseTimer = undefined;
    }, 1_200);
  }

  private cancelRecurringPreviewDebounce(): void {
    if (this.recurringPreviewDebounceTimer !== undefined) {
      window.clearTimeout(this.recurringPreviewDebounceTimer);
      this.recurringPreviewDebounceTimer = undefined;
    }
    this.reviewRefreshPending = false;
  }

  private clearStatusBusy(): void {
    const status = this.root.querySelector('[role="status"]') as HTMLElement | null;
    if (!status) return;
    status.setAttribute("aria-busy", "false");
    status.classList.remove("busy");
    status.querySelector(".spinner")?.remove();
  }

  private cancelRecurringPreviewRequest(options: { clearPreview?: boolean } = {}): void {
    this.cancelRecurringPreviewDebounce();
    this.recurringPreviewRequestSequence += 1;
    this.recurringPreviewAbort?.abort();
    this.recurringPreviewAbort = undefined;
    this.recurringPricingLoading = false;
    if (options.clearPreview) this.recurringPreview = undefined;
    this.renderRecurringPreview();
    this.clearStatusBusy();
    this.updateStepUI();
  }

  private scheduleRecurringPreviewRefresh(form: HTMLFormElement): void {
    if (!this.selectedSlot || !this.serviceRecurrence()?.enabled || !["recurrence", "information", "payment"].includes(this.currentStep)) return;
    this.cancelRecurringPreviewDebounce();
    this.reviewRefreshPending = true;
    const submit = this.root.querySelector<HTMLButtonElement>('[data-action="continue"]');
    if (submit) submit.disabled = true;
    this.setStatus("Verificando disponibilidad y precio…", false, true);
    this.recurringPreviewDebounceTimer = window.setTimeout(() => {
      this.recurringPreviewDebounceTimer = undefined;
      if (!this.validateRecurringOccurrenceInputs()) {
        this.reviewRefreshPending = false;
        this.updateStepUI();
        return;
      }
      void this.refreshRecurringPreviewForSelection(form);
    }, 350);
  }

  private scheduleReviewQuoteRefresh(form: HTMLFormElement): void {
    this.cancelRecurringPreviewDebounce();
    this.reviewRefreshPending = true;
    const submit = this.root.querySelector<HTMLButtonElement>('[data-action="continue"]');
    if (submit) submit.disabled = true;
    if (!this.recurringPreview) this.oneOffQuote = undefined;
    this.setStatus("Verificando disponibilidad y precio…", false, true);
    this.recurringPreviewDebounceTimer = window.setTimeout(() => {
      this.recurringPreviewDebounceTimer = undefined;
      if (this.recurringPreview || (form.elements.namedItem("recurringEnabled") as HTMLInputElement | null)?.checked) {
        void this.refreshRecurringPreviewForSelection(form);
      } else {
        void this.refreshOneOffQuote(form);
      }
    }, 350);
  }

  private async refreshOneOffQuote(form: HTMLFormElement): Promise<void> {
    const slot = this.selectedSlot;
    const service = this.services.find((item) => item.id === this.serviceIdFromForm());
    if (!slot || !service || !this.client) {
      this.reviewRefreshPending = false;
      this.updateStepUI();
      return;
    }
    const sequence = ++this.oneOffQuoteRequestSequence;
    this.reviewQuoteAbort?.abort();
    const controller = new AbortController();
    this.reviewQuoteAbort = controller;
    this.oneOffQuoteLoading = true;
    this.setStatus("Verificando disponibilidad y precio…", false, true);
    this.updateStepUI();
    try {
      const persons = Math.max(1, Number((form.elements.namedItem("persons") as HTMLInputElement | null)?.value || 1) || 1);
      const endAt = this.oneOffEndAt(form, slot, service);
      const quote = await this.client.quoteService({
        serviceId: service.id,
        providerId: slot.providerId ?? null,
        locationId: slot.locationId ?? null,
        startAt: slot.startAt,
        endAt,
        persons,
        extras: this.selectedExtrasFromForm(form),
        couponCode: this.couponCodeFromForm(form),
        slotKey: slot.slotKey,
        timezone: slot.timezone ?? "UTC",
        idempotencyKey: randomIdempotency("public-review-quote")
      }, { signal: controller.signal });
      if (sequence !== this.oneOffQuoteRequestSequence) return;
      this.oneOffQuote = quote;
      this.highlightPrices();
      this.setStatus("Precio actualizado. Revisá el detalle antes de confirmar.");
    } catch (error) {
      if (sequence !== this.oneOffQuoteRequestSequence || isAbortError(error)) return;
      this.setStatus(this.errorMessage(error), true);
    } finally {
      if (this.reviewQuoteAbort === controller) this.reviewQuoteAbort = undefined;
      if (sequence === this.oneOffQuoteRequestSequence) {
        this.oneOffQuoteLoading = false;
        this.reviewRefreshPending = false;
        this.updateStepUI();
      }
    }
  }

  private async refreshRecurringPreviewForSelection(form: HTMLFormElement): Promise<void> {
    const preview = this.recurringPreview;
    const slot = this.selectedSlot;
    if (!slot || !this.client) {
      this.recurringPricingLoading = false;
      this.reviewRefreshPending = false;
      this.updateStepUI();
      return;
    }
    const previousSelections = preview ? this.selectedRecurringOccurrences() : undefined;
    const sequence = ++this.recurringPreviewRequestSequence;
    this.recurringPreviewAbort?.abort();
    const controller = new AbortController();
    this.recurringPreviewAbort = controller;
    this.recurringPricingLoading = true;
    this.renderRecurringPreview();
    this.updateStepUI();
    let timedOut = false;
    const timeout = window.setTimeout(() => { timedOut = true; controller.abort(); }, 15_000);
    try {
      this.setStatus("Verificando disponibilidad y precio de las fechas…", false, true);
      const rule = this.recurringRuleFromForm(form);
      const values = Object.fromEntries(new FormData(form).entries());
      const refreshed = await this.client.previewRecurringSeries({
        serviceId: this.serviceIdFromForm(), providerId: slot.providerId ?? null, locationId: slot.locationId ?? null,
        timezone: slot.timezone ?? "UTC", startsAt: slot.startAt, count: this.recurringCountFromForm(form, preview?.occurrences.length ?? 4), rule,
        persons: Math.max(1, Number(values.persons ?? 1) || 1), participants: this.recurringParticipantsFromForm(form),
        extras: this.selectedExtrasFromForm(form, true), couponCode: this.couponCodeFromForm(form),
        occurrences: previousSelections
      }, { signal: controller.signal });
      if (sequence !== this.recurringPreviewRequestSequence) return;
      this.recurringPreview = refreshed;
      this.renderRecurringPreview();
      this.highlightPrices();
      this.setStatus("Disponibilidad y precio actualizados. Revisá cualquier ajuste antes de continuar.");
    } catch (error) {
      if (sequence !== this.recurringPreviewRequestSequence) return;
      this.setStatus(timedOut ? "La previsualización tardó demasiado. Reintentá." : this.errorMessage(error), true);
    } finally {
      window.clearTimeout(timeout);
      if (this.recurringPreviewAbort === controller) this.recurringPreviewAbort = undefined;
      if (sequence === this.recurringPreviewRequestSequence) {
        this.recurringPricingLoading = false;
        this.reviewRefreshPending = false;
        this.renderRecurringPreview();
        this.updateStepUI();
      }
    }
  }

  private async goToStep(target: BookingStep): Promise<void> {
    const targetIndex = bookingSteps.findIndex((step) => step.key === target);
    const currentIndex = bookingSteps.findIndex((step) => step.key === this.currentStep);
    if (target === "confirmation" || targetIndex < 0 || targetIndex > currentIndex) return;
    if (target === "information" && !this.pendingHold && !this.recurringPreview) { this.setStatus("Elegí un horario y continuá para completar tus datos.", true); return; }
    if (target === "payment" && !this.recurringCheckout && !this.bookingCheckout && !this.selectedSlot) { this.setStatus("Elegí un horario antes de revisar el pago.", true); return; }
    this.currentStep = target;
    this.updateStepUI();
  }

  private async goPrevious(): Promise<void> {
    if (this.currentStep === "confirmation" || this.currentStep === "datetime") return;
    if (this.currentStep === "information") {
      if (this.recurringPreview) this.currentStep = "recurrence";
      else { this.emitBookingCancelled("user_cancelled"); await this.releasePendingHold(); this.currentStep = "datetime"; }
    } else if (this.currentStep === "recurrence") {
      this.emitBookingCancelled("user_cancelled");
      this.cancelRecurringPreviewRequest({ clearPreview: true });
      this.currentStep = "datetime";
    } else if (this.currentStep === "payment") {
      if (this.bookingCheckout) {
        this.setStatus("La reserva está pendiente de pago. Abrí el enlace de Mercado Pago para continuar.");
        return;
      }
      this.currentStep = "information";
    }
    this.updateStepUI();
  }

  private changeMonth(offset: number): void {
    const date = monthStart(this.calendarMonth);
    date.setMonth(date.getMonth() + offset);
    this.calendarMonth = monthValue(date);
    this.renderCalendar();
    void this.loadCalendarSummary();
  }

  private renderCalendar(): void {
    const grid = this.root.querySelector("[data-calendar-grid]") as HTMLElement | null;
    const monthSelect = this.root.querySelector("[data-calendar-month]") as HTMLSelectElement | null;
    const yearSelect = this.root.querySelector("[data-calendar-year]") as HTMLSelectElement | null;
    if (!grid || !monthSelect || !yearSelect) return;
    const visibleMonth = monthStart(this.calendarMonth);
    const locale = this.getAttribute("locale") ?? "es-ES";
    monthSelect.innerHTML = renderCalendarMonthOptions(visibleMonth, locale);
    monthSelect.value = String(visibleMonth.getMonth());
    yearSelect.innerHTML = renderCalendarYearOptions(visibleMonth);
    yearSelect.value = String(visibleMonth.getFullYear());
    const changeMonth = () => {
      this.calendarMonth = `${yearSelect.value}-${String(Number(monthSelect.value) + 1).padStart(2, "0")}`;
      this.renderCalendar();
      void this.loadCalendarSummary();
    };
    monthSelect.onchange = changeMonth;
    yearSelect.onchange = changeMonth;
    grid.innerHTML = renderCalendarCells({ visibleMonth, today: localDateValue(), selectedDate: this.selectedDate, availabilityDays: this.availabilityDays, locale }).join("");
    grid.querySelectorAll("[data-calendar-date]").forEach((node) => node.addEventListener("click", () => {
      const value = (node as HTMLElement).dataset.calendarDate;
      if (!value) return;
      this.selectedDate = value;
      this.calendarMonth = value.slice(0, 7);
      const dateInput = this.root.querySelector('input[name="date"]') as HTMLInputElement | null;
      if (dateInput) dateInput.value = value;
       this.currentStep = "datetime";
       this.renderCalendar();
       void this.loadSlots();
       window.setTimeout(() => this.root.querySelector("[data-slots]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    }));
  }

  private renderServiceScopedFilters(form: HTMLFormElement, providerRows: CatalogProvider[], locationRows: CatalogLocation[], selectedProviderId?: string, selectedLocationId?: string): void {
    const serviceId = this.serviceIdFromForm();
    const providerSelect = form.elements.namedItem("providerId") as HTMLSelectElement | null;
    if (providerSelect) {
      providerSelect.innerHTML = "";
      providerSelect.append(new Option(
        !serviceId ? "Elegí un servicio primero" : providerRows.length ? "Todos los profesionales" : "No hay profesionales para este servicio",
        ""
      ));
      providerRows.forEach((provider) => providerSelect.append(new Option(provider.display_name, provider.id)));
      providerSelect.value = selectedProviderId && providerRows.some((provider) => provider.id === selectedProviderId) ? selectedProviderId : "";
      providerSelect.disabled = !serviceId || providerRows.length === 0;
    }
    const locationSelect = form.elements.namedItem("locationId") as HTMLSelectElement | null;
    if (locationSelect) {
      locationSelect.innerHTML = "";
      locationSelect.append(new Option(
        !serviceId ? "Elegí un servicio primero" : locationRows.length ? "Todas las sedes" : "No hay sedes para este servicio",
        ""
      ));
      locationRows.forEach((location) => locationSelect.append(new Option(`${location.name} · ${location.timezone}`, location.id)));
      locationSelect.value = selectedLocationId && locationRows.some((location) => location.id === selectedLocationId) ? selectedLocationId : "";
      locationSelect.disabled = !serviceId || locationRows.length === 0;
    }
  }

  private async loadCatalogAndSlots(): Promise<void> {
    if (!this.client) return;
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    if (!form) return;
    this.catalogAbort?.abort();
    const controller = new AbortController();
    this.catalogAbort = controller;
    const sequence = ++this.catalogRequestSequence;
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    try {
      const serviceSelect = form.elements.namedItem("serviceId") as HTMLSelectElement;
      const providerSelect = form.elements.namedItem("providerId") as HTMLSelectElement | null;
      const locationSelect = form.elements.namedItem("locationId") as HTMLSelectElement | null;
      const selectedServiceId = this.getAttribute("service-id") ?? serviceSelect.value;
      const selectedProviderId = (this.getAttribute("provider-id") ?? providerSelect?.value) || undefined;
      const selectedLocationId = (this.getAttribute("location-id") ?? locationSelect?.value) || undefined;
      const serviceRows = await this.client.services({ signal: controller.signal });
      if (sequence !== this.catalogRequestSequence) return;
      this.services = serviceRows;
      const retry = this.root.querySelector("[data-catalog-retry]") as HTMLElement | null;
      if (retry) retry.hidden = true;
      if (this.getAttribute("service-id")) {
        const service = serviceRows.find((item) => item.id === selectedServiceId);
        serviceSelect.innerHTML = `<option value="${escapeHtml(selectedServiceId)}">${escapeHtml(service?.name ?? "Servicio seleccionado")}</option>`;
        serviceSelect.value = selectedServiceId;
        serviceSelect.disabled = true;
      } else {
        serviceSelect.innerHTML = `<option value="">Seleccioná un servicio</option>`;
        serviceRows.forEach((service) => serviceSelect.append(new Option(`${service.name} · ${service.durationMinutes} min`, service.id)));
        if (selectedServiceId && serviceRows.some((service) => service.id === selectedServiceId)) serviceSelect.value = selectedServiceId;
      }
      const serviceId = this.getAttribute("service-id") ?? serviceSelect.value;
      this.emitServiceSelected(serviceId);
      let providerRows: CatalogProvider[] = [];
      let locationRows: CatalogLocation[] = [];
      if (serviceId) {
        [providerRows, locationRows] = await Promise.all([
          this.client.providers({ serviceId, signal: controller.signal }).catch((error) => {
            if (isAbortError(error)) throw error;
            return [];
          }),
          this.client.locations({ serviceId, providerId: selectedProviderId, signal: controller.signal }).catch((error) => {
            if (isAbortError(error)) throw error;
            return [];
          })
        ]);
      }
      if (sequence !== this.catalogRequestSequence) return;
      this.providers = providerRows;
      this.locations = locationRows;
      this.renderServiceScopedFilters(form, providerRows, locationRows, selectedProviderId, selectedLocationId);
      const filters = form.querySelector(".filter-grid") as HTMLElement | null;
      if (filters) filters.hidden = false;
      this.updateServiceSummary(form);
      if (serviceId) {
        this.extras = await this.client.extras(serviceId, { signal: controller.signal }).catch((error) => {
          if (isAbortError(error)) throw error;
          return [];
        });
        if (sequence !== this.catalogRequestSequence) return;
        this.renderExtras(form);
        this.updateRecurrenceAvailability(form);
        this.updateStepUI();
        await this.loadSlots();
      } else {
        this.extras = [];
        this.renderExtras(form);
        this.currentStep = "datetime";
        this.setStatus("Elegí un servicio para consultar horarios.");
        this.renderSlots([]);
        this.updateStepUI();
      }
    } catch (error) {
      if (sequence !== this.catalogRequestSequence) return;
      const message = isAbortError(error) ? "La consulta del catálogo tardó demasiado." : this.errorMessage(error);
      this.setStatus(`${message} Usá Reintentar para volver a consultar.`, true);
      const serviceSelect = form.elements.namedItem("serviceId") as HTMLSelectElement | null;
      if (serviceSelect && !this.getAttribute("service-id")) serviceSelect.innerHTML = "<option value=\"\">No se pudo cargar el catálogo</option>";
      const retry = this.root.querySelector("[data-catalog-retry]") as HTMLElement | null;
      if (retry) retry.hidden = false;
      const choice = this.root.querySelector("[data-service-choice]") as HTMLElement | null;
      if (choice) choice.hidden = false;
      const slotsContainer = form.querySelector("[data-slots]") as HTMLElement | null;
      if (slotsContainer) {
        slotsContainer.innerHTML = `<div class="alert">${escapeHtml(message)}</div>`;
      }
    } finally {
      window.clearTimeout(timeout);
      if (this.catalogAbort === controller) this.catalogAbort = undefined;
    }
  }

  private updateServiceSummary(form: HTMLFormElement): void {
    const serviceId = this.getAttribute("service-id") ?? (form.elements.namedItem("serviceId") as HTMLSelectElement | null)?.value;
    const service = this.services.find((item) => item.id === serviceId);
    const meta = form.querySelector("[data-service-meta]") as HTMLElement | null;
    const choice = form.querySelector("[data-service-choice]") as HTMLElement | null;
    const description = form.querySelector("[data-service-description]") as HTMLElement | null;
    const payment = form.querySelector("[data-service-payment]") as HTMLElement | null;
    const price = form.querySelector("[data-service-price]") as HTMLElement | null;
    if (choice) choice.hidden = !service;
    if (!meta || !description || !price) { this.updateRecurrenceAvailability(form); return; }
    meta.hidden = !service;
    if (service) {
      description.textContent = `${service.description || "Duración"} · ${service.durationMinutes} min`;
      if (payment) payment.textContent = service.paymentMode === "prepay" ? "Mercado Pago · prepago" : "Pago posterior";
      price.innerHTML = this.renderStaticPriceTooltip(service.priceCents, service.currency, "Precio base configurado para el servicio.", "Precio base del servicio");
      this.bindPriceTooltips(price);
    }
    this.updateRecurrenceAvailability(form);
  }

  private async handleServiceChange(form: HTMLFormElement, serviceId: string): Promise<void> {
    this.oneOffQuote = undefined;
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    this.recurringPreview = undefined;
    const providerSelect = form.elements.namedItem("providerId") as HTMLSelectElement | null;
    const locationSelect = form.elements.namedItem("locationId") as HTMLSelectElement | null;
    if (providerSelect && !this.getAttribute("provider-id")) providerSelect.value = "";
    if (locationSelect && !this.getAttribute("location-id")) locationSelect.value = "";
    this.providers = [];
    this.locations = [];
    this.renderServiceScopedFilters(form, [], []);
    this.extras = [];
    this.renderExtras(form);
    this.updateServiceSummary(form);
    if (!serviceId) {
      this.currentStep = "datetime";
      this.renderSlots([]);
      this.updateStepUI();
      this.setStatus("Elegí un servicio para consultar horarios.");
      return;
    }
    this.currentStep = "datetime";
    this.updateStepUI();
    this.setStatus("Cargando opciones del servicio…", false, true);
    await this.loadCatalogAndSlots();
  }

  private async loadServiceExtras(form: HTMLFormElement, serviceId: string): Promise<void> {
    if (!this.client) return;
    this.serviceExtrasAbort?.abort();
    const controller = new AbortController();
    this.serviceExtrasAbort = controller;
    const sequence = ++this.serviceExtrasRequestSequence;
    try {
      const extras = await this.client.extras(serviceId, { signal: controller.signal });
      if (sequence !== this.serviceExtrasRequestSequence || this.serviceIdFromForm() !== serviceId) return;
      this.extras = extras;
      this.renderExtras(form);
      this.updateRecurrenceAvailability(form);
    } catch (error) {
      if (sequence !== this.serviceExtrasRequestSequence || isAbortError(error)) return;
      this.extras = [];
      this.renderExtras(form);
      this.updateRecurrenceAvailability(form);
      this.setStatus("No pudimos cargar los extras del servicio. Podés continuar sin extras.", true);
    } finally {
      if (this.serviceExtrasAbort === controller) this.serviceExtrasAbort = undefined;
    }
  }

  private renderExtras(form: HTMLFormElement): void {
    const wrapper = form.querySelector("[data-extras]") as HTMLElement | null;
    const list = form.querySelector("[data-extra-list]") as HTMLElement | null;
    if (!wrapper || !list) return;
    const recurrenceToggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    list.innerHTML = "";
    wrapper.hidden = !this.extras.length || Boolean(recurrenceToggle?.checked);
    const serviceId = this.serviceIdFromForm();
    const currency = this.services.find((service) => service.id === serviceId)?.currency ?? "EUR";
    if (!wrapper.hidden) {
      list.innerHTML = this.extras.map((extra) => `<label class="extra-option"><input type="checkbox" name="extra-${escapeHtml(extra.id)}" value="${escapeHtml(extra.id)}" /> <span>${escapeHtml(extra.name)} <small>+${this.renderStaticPriceTooltip(extra.price_cents, currency, "Extra agregado a esta reserva.", "Precio del extra")}</small></span></label>`).join("");
      this.bindPriceTooltips(list);
      list.querySelectorAll<HTMLInputElement>('input[name^="extra-"]').forEach((input) => input.addEventListener("change", () => {
        this.oneOffQuote = undefined;
        if (this.currentStep === "payment") this.scheduleReviewQuoteRefresh(form);
      }));
    }
    this.renderRecurrenceExtras(form);
  }

  private async loadSlots(options: { preserveStatus?: boolean } = {}): Promise<void> {
    if (!this.client) return;
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    if (!form) return;
    const serviceId = this.serviceIdFromForm();
    const dateInput = form.elements.namedItem("date") as HTMLInputElement;
    const date = dateInput.value || this.selectedDate;
    const providerId = (this.getAttribute("provider-id") ?? (form.elements.namedItem("providerId") as HTMLSelectElement).value) || undefined;
    const locationId = (this.getAttribute("location-id") ?? (form.elements.namedItem("locationId") as HTMLSelectElement).value) || undefined;
    const slotsContainer = form.querySelector("[data-slots]") as HTMLElement;
    if (!serviceId) { this.setStatus("Elegí un servicio para consultar horarios."); this.renderSlots([]); this.updateStepUI(); return; }
    if (!date) { this.setStatus("Elegí una fecha para consultar horarios."); this.renderSlots([]); this.updateStepUI(); return; }
    this.selectedDate = date;
    this.calendarMonth = date.slice(0, 7);
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    this.oneOffQuote = undefined;
    this.slotsAbort?.abort();
    const controller = new AbortController(); this.slotsAbort = controller;
    const sequence = ++this.requestSequence;
    await this.releasePendingHold();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    this.availabilityLoading = true;
    slotsContainer.setAttribute("aria-busy", "true");
    slotsContainer.innerHTML = '<div class="loading-skeleton" role="status"><span style="width:68%"></span><span style="width:84%"></span><span style="width:51%"></span></div><div class="slots-empty"><span class="spinner" aria-hidden="true"></span> Verificando disponibilidad…</div>';
    this.selectedSlot = undefined;
    this.updateStepUI();
    if (!options.preserveStatus) this.setStatus("Consultando disponibilidad…", false, true);
    try {
      const persons = Math.max(1, Number((form.elements.namedItem("persons") as HTMLInputElement | null)?.value || 1) || 1);
      const extras = this.selectedExtrasFromForm(form);
      let slots = await this.client.slots(serviceId, date, { providerId, locationId, persons, extras, includePricing: true, signal: controller.signal });
      if (sequence !== this.requestSequence) return;
      if (!slots.length && !providerId && !locationId) {
        const next = await this.client.nextAvailability(serviceId, { fromDate: date, days: 14, signal: controller.signal });
        if (next.nextDate && next.nextDate !== date) {
          this.selectedDate = next.nextDate;
          this.calendarMonth = next.nextDate.slice(0, 7);
          dateInput.value = next.nextDate;
          if (!options.preserveStatus) this.setStatus(`No había horarios el ${date}. Mostramos la próxima fecha disponible: ${next.nextDate}.`);
          slots = await this.client.slots(serviceId, next.nextDate, { providerId, locationId, persons, extras, includePricing: true, signal: controller.signal });
        }
      }
      if (sequence !== this.requestSequence) return;
      this.availabilityDays.set(this.selectedDate, slots.length);
      this.renderCalendar();
      this.renderSlots(slots);
      this.emitBookingAnalytics("slot_viewed", { actor: "anonymous", step: "datetime", date: this.selectedDate, slotCount: slots.length });
      if (!options.preserveStatus) this.setStatus(slots.length ? `${slots.length} horarios disponibles.` : "No hay horarios disponibles. Probá otra fecha o filtro.");
      this.dispatchEvent(new CustomEvent("kuroo:availability-loaded", { bubbles: true, composed: true, detail: { serviceId, date: this.selectedDate, slots } }));
    } catch (error) {
      if (sequence !== this.requestSequence) return;
      this.renderSlots([], true);
      this.emitSlotConflict(error);
      if (!options.preserveStatus) this.setStatus(isAbortError(error) ? "La consulta tardó demasiado. Reintentá." : this.errorMessage(error), true);
      this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "availability", error } }));
    } finally {
      window.clearTimeout(timeout);
      if (this.slotsAbort === controller) this.slotsAbort = undefined;
      this.availabilityLoading = false;
      slotsContainer.removeAttribute("aria-busy");
      this.updateStepUI();
    }
  }

  private async loadCalendarSummary(): Promise<void> {
    const serviceId = this.serviceIdFromForm();
    if (!this.client || !serviceId) return;
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    const providerId = (this.getAttribute("provider-id") ?? (form?.elements.namedItem("providerId") as HTMLSelectElement | null)?.value) || undefined;
    const locationId = (this.getAttribute("location-id") ?? (form?.elements.namedItem("locationId") as HTMLSelectElement | null)?.value) || undefined;
    this.calendarAbort?.abort();
    const controller = new AbortController();
    this.calendarAbort = controller;
    const sequence = ++this.calendarRequestSequence;
    try {
      const result = await this.client.nextAvailability(serviceId, { fromDate: `${this.calendarMonth}-01`, days: 42, providerId, locationId, signal: controller.signal });
      if (sequence !== this.calendarRequestSequence) return;
      result.dates.forEach((entry) => this.availabilityDays.set(entry.date, entry.count));
      this.renderCalendar();
    } catch (error) {
      if (!isAbortError(error)) this.setStatus("No pudimos actualizar los días disponibles.");
    } finally {
      if (this.calendarAbort === controller) this.calendarAbort = undefined;
    }
  }

  private renderSlots(slots: AvailableSlot[], failed = false): void {
    const container = this.root.querySelector("[data-slots]") as HTMLElement | null;
    if (!container) return;
    this.selectedSlot = undefined;
    container.innerHTML = renderBookingSlotsMarkup(slots, {
      failed,
      waitlistEnabled: this.waitlistEnabled(),
      serviceId: this.serviceIdFromForm(),
      formatSlot: (slot) => this.formatSlot(slot),
      renderSlotPrice: (slot) => slot.quote ? this.renderPriceTooltip(slot.quote.totalCents, slot.quote.currency, slot.quote, "Precio estimado del turno") : ""
    });
    if (failed) { container.querySelector("[data-retry]")?.addEventListener("click", () => void this.loadSlots()); return; }
    if (!slots.length) {
      this.bindWaitlist(container);
      this.updateStepUI();
      return;
    }
    container.querySelectorAll("[data-slot-index]").forEach((button) => button.addEventListener("click", () => {
      const slotIndex = Number((button as HTMLElement).dataset.slotIndex);
      const slot = Number.isInteger(slotIndex) && slotIndex >= 0 ? slots[slotIndex] : undefined; if (!slot) return;
       container.querySelectorAll(".slot").forEach((item) => item.classList.remove("selected")); button.classList.add("selected"); this.selectedSlot = slot; this.oneOffQuote = slot.quote ?? undefined;
      this.currentStep = "datetime";
      this.updateStepUI();
      this.setStatus(`Seleccionaste ${this.formatSlot(slot)}. Continuá para reservarlo temporalmente.`);
    }));
    this.bindPriceTooltips(container);
  }

  private bindWaitlist(container: HTMLElement): void {
    const open = container.querySelector('[data-action="open-waitlist"]') as HTMLButtonElement | null;
    const panel = container.querySelector("[data-waitlist-panel]") as HTMLElement | null;
    const email = container.querySelector("[data-waitlist-email]") as HTMLInputElement | null;
    const persons = container.querySelector("[data-waitlist-persons]") as HTMLInputElement | null;
    const submit = container.querySelector('[data-action="join-waitlist"]') as HTMLButtonElement | null;
    const status = container.querySelector("[data-waitlist-status]") as HTMLElement | null;
    if (!open || !panel || !email || !persons || !submit || !status) return;
    open.addEventListener("click", () => {
      open.hidden = true;
      panel.hidden = false;
      this.emitBookingAnalytics("waitlist_opened", { actor: "anonymous", step: "datetime", date: this.selectedDate });
      email.focus();
    });
    submit.addEventListener("click", async () => {
      if (!this.client || !email.checkValidity() || !persons.checkValidity()) {
        email.reportValidity();
        persons.reportValidity();
        return;
      }
      submit.disabled = true;
      status.textContent = "Guardando tu solicitud…";
      try {
        const result = await this.client.joinWaitlist({
          serviceId: this.serviceIdFromForm(),
          email: email.value.trim(),
          persons: Math.max(1, Number(persons.value) || 1),
          desiredStartAt: `${this.selectedDate}T00:00:00.000Z`
        });
        status.textContent = "Listo. Te avisaremos si aparece disponibilidad.";
        submit.hidden = true;
        this.dispatchEvent(new CustomEvent("kuroo:waitlist-joined", { bubbles: true, composed: true, detail: result }));
      } catch (error) {
        status.textContent = this.errorMessage(error);
        this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "waitlist", error } }));
      } finally {
        submit.disabled = false;
      }
    });
  }

  private async findNextDate(): Promise<void> {
    const serviceId = this.serviceIdFromForm();
    if (!serviceId || !this.client) { this.setStatus("Elegí un servicio primero.", true); return; }
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    const providerId = (this.getAttribute("provider-id") ?? (form?.elements.namedItem("providerId") as HTMLSelectElement | null)?.value) || undefined;
    const locationId = (this.getAttribute("location-id") ?? (form?.elements.namedItem("locationId") as HTMLSelectElement | null)?.value) || undefined;
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    const fromDate = this.selectedDate || localDateValue();
    const button = this.root.querySelector('[data-action="next-date"]') as HTMLButtonElement;
    if (button) button.disabled = true;
    this.setStatus("Buscando el próximo horario…", false, true);
    this.slotsAbort?.abort();
    this.requestSequence += 1;
    const controller = new AbortController();
    this.slotsAbort = controller;
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    try {
      const next = await this.client.nextAvailability(serviceId, { fromDate, days: 90, providerId, locationId, signal: controller.signal });
      next.dates.forEach((entry) => this.availabilityDays.set(entry.date, entry.count));
      if (!next.nextDate) { this.setStatus("No encontramos disponibilidad en los próximos 90 días.", true); return; }
      this.selectedDate = next.nextDate;
      this.calendarMonth = next.nextDate.slice(0, 7);
      const dateInput = this.root.querySelector('input[name="date"]') as HTMLInputElement | null;
      if (dateInput) dateInput.value = next.nextDate;
      this.renderCalendar();
      await this.loadSlots();
    } catch (error) { this.setStatus(isAbortError(error) ? "La búsqueda tardó demasiado. Reintentá." : this.errorMessage(error), true); }
    finally { window.clearTimeout(timeout); if (this.slotsAbort === controller) this.slotsAbort = undefined; if (button) button.disabled = false; }
  }

  private formatSlot(slot: AvailableSlot): string {
    return formatSlot(slot, this.getAttribute("locale") ?? "es-ES");
  }

  private formatOccurrenceDateTime(value: string, timezone: string): string {
    return formatOccurrenceDateTime(value, timezone, this.getAttribute("locale") ?? "es-ES");
  }

  private adjustmentReasonText(reason: string | null | undefined): string {
    return adjustmentReasonText(reason);
  }

  private formatMoney(cents: number, currency: string): string {
    return formatMoney(cents, currency, this.getAttribute("locale") ?? "es-AR");
  }

  private setStatus(message: string, isError = false, busy = false): void {
    const status = this.root.querySelector('[role="status"]') as HTMLElement | null;
    if (!status) return;
    const isBusy = !isError && busy;
    status.innerHTML = isBusy ? `<span class="spinner" aria-hidden="true"></span> ${escapeHtml(message)}` : escapeHtml(message);
    status.setAttribute("aria-busy", String(isBusy));
    status.classList.toggle("busy", isBusy);
    status.classList.toggle("error", isError);
  }

  private errorMessage(error: unknown): string {
    return errorMessage(error);
  }

  private async resetBooking(): Promise<void> {
    if (this.currentStep !== "datetime") this.emitBookingCancelled("user_cancelled");
    this.beginAnalyticsFlow();
    this.catalogAbort?.abort();
    this.catalogRequestSequence += 1;
    this.slotsAbort?.abort();
    this.calendarAbort?.abort();
    this.cancelRecurringPreviewRequest({ clearPreview: true });
    this.reviewQuoteAbort?.abort();
    this.requestSequence += 1;
    this.oneOffQuoteRequestSequence += 1;
    const release = this.releasePendingHold();
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollTimer = undefined;
    this.paymentPollAttempts = 0;
    this.selectedSlot = undefined;
    this.weeklyDaysCustomized = false;
    this.recurringCheckout = undefined;
    this.bookingCheckout = undefined;
    this.bookingPaymentStatus = undefined;
    this.oneOffQuote = undefined;
    this.oneOffQuoteLoading = false;
    this.availabilityLoading = false;
    this.recurringPricingLoading = false;
    this.paymentRefreshing = false;
    this.reviewRefreshPending = false;
    this.lastBooking = undefined;
    this.selectedDate = localDateValue();
    this.calendarMonth = monthValue(new Date());
    this.availabilityDays.clear();
    this.currentStep = "datetime";
    this.render();
    this.emitBookingAnalytics("booking_started", { actor: "anonymous", step: "datetime" });
    await release;
    await this.loadCatalogAndSlots();
  }

  private async releasePendingHold(): Promise<void> {
    const hold = this.pendingHold; this.pendingHold = undefined; if (this.countdownTimer) window.clearInterval(this.countdownTimer); this.countdownTimer = undefined;
    if (hold && this.client) {
      const release = this.client.releaseHold(hold.holdId, hold.slotKey, hold.quote?.id ?? undefined, hold.holdCapability).catch(() => undefined);
      await Promise.race([release, new Promise<void>((resolve) => window.setTimeout(resolve, 5_000))]);
    }
    const banner = this.root.querySelector("[data-hold]") as HTMLElement | null; if (banner) banner.hidden = true;
    if (hold) {
      this.selectedSlot = undefined;
      const customer = this.root.querySelector("[data-customer]") as HTMLElement | null;
      const empty = this.root.querySelector("[data-customer-empty]") as HTMLElement | null;
      if (customer) customer.hidden = true;
      if (empty) empty.hidden = false;
      if (this.currentStep !== "confirmation") this.currentStep = "datetime";
      this.updateStepUI();
    }
  }

  private startCountdown(expiresAt: string, form: HTMLFormElement): void {
    const banner = form.querySelector("[data-hold]") as HTMLElement; const counter = form.querySelector("[data-countdown]") as HTMLElement; banner.hidden = false;
    const tick = () => {
      const seconds = Math.max(0, Math.ceil((Date.parse(expiresAt) - Date.now()) / 1000));
      counter.textContent = `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;
      if (seconds <= 0) {
        this.selectedSlot = undefined;
        this.emitBookingCancelled("hold_expired");
        void this.releasePendingHold().then(() => this.setStatus("El hold expiró. Elegí otro horario para continuar.", true));
      }
    };
    tick(); if (this.countdownTimer) window.clearInterval(this.countdownTimer); this.countdownTimer = window.setInterval(tick, 1000);
  }

  private renderConfirmation(): void {
    const detail = this.root.querySelector("[data-confirmation-detail]") as HTMLElement | null;
    const locale = this.getAttribute("locale") ?? "es-ES";
    if (!detail) return;
    const series = this.recurringCheckout?.series;
    if (series) {
      const selected = series.occurrences.filter((occurrence) => occurrence.status !== "cancelled" && occurrence.status !== "unavailable");
      const first = selected[0];
      const dates = first ? new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short", timeZone: series.timezone }).format(new Date(first.startAt)) : "";
      const currency = series.currency ?? this.recurringPreview?.currency ?? "EUR";
      const total = series.totalCents ?? this.recurringPreview?.totalCents ?? 0;
      detail.innerHTML = `<strong>Serie de ${selected.length} ocurrencia${selected.length === 1 ? "" : "s"}</strong><br>${escapeHtml(dates)}${selected.length > 1 ? " y fechas posteriores" : ""}<br>Total: ${this.renderPriceTooltip(total, currency, this.recurringPreview?.pricing ?? null, "Total de la serie")}<br>Serie: <strong>${escapeHtml(series.id.slice(0, 8))}</strong>`;
      this.bindPriceTooltips(detail);
      return;
    }
    if (this.lastBooking) {
      const service = this.services.find((item) => item.id === this.lastBooking?.serviceId);
      const date = new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short" }).format(new Date(this.lastBooking.startAt));
      const total = this.lastBooking.priceCents + (this.lastBooking.taxCents ?? 0);
      detail.innerHTML = `<strong>${escapeHtml(service?.name ?? "Reserva")}</strong><br>${escapeHtml(date)}<br>Total: ${this.renderPriceTooltip(total, this.lastBooking.currency, this.oneOffQuote, "Total de la reserva")}<br>Código: <strong>${escapeHtml(this.lastBooking.id.slice(0, 8))}</strong>`;
      this.bindPriceTooltips(detail);
    }
  }

  private renderRecurringPayment(): void {
    const content = this.root.querySelector("[data-payment-content]") as HTMLElement | null;
    const checkout = this.recurringCheckout;
    if (!content || !checkout) return;
    const state = checkout.series.paymentState ?? "pending";
    const currency = state === "paid"
      ? checkout.series.currency ?? this.recurringPreview?.currency ?? "EUR"
      : checkout.payment?.currency ?? checkout.series.currency ?? this.recurringPreview?.currency ?? "EUR";
    const total = state === "paid"
      ? checkout.series.totalCents ?? this.recurringPreview?.totalCents ?? 0
      : checkout.payment?.amountCents ?? checkout.series.totalCents ?? 0;
    const result = renderRecurringPaymentMarkup({
      state,
      occurrenceCount: checkout.series.occurrences.length,
      priceTooltip: state === "failed" ? "" : this.renderPriceTooltip(total, currency, this.recurringPreview?.pricing ?? null, "Total de la serie"),
      checkoutUrl: checkout.payment?.checkoutUrl,
      paymentRefreshing: this.paymentRefreshing
    });
    content.innerHTML = result.html;
    if (state === "paid") {
      this.bindPriceTooltips(content);
      return;
    }
    content.querySelector('[data-action="payment-refresh"]')?.addEventListener("click", () => void this.refreshRecurringPayment(true));
  }

  private renderReviewPayment(): void {
    const content = this.root.querySelector("[data-payment-content]") as HTMLElement | null;
    const form = this.root.querySelector("form") as HTMLFormElement | null;
    if (!content || !form) return;
    const previousCoupon = form.querySelector<HTMLInputElement>("[data-review-coupon]")?.value ?? "";
    const preview = this.recurringPreview;
    if (preview) {
      const selected = this.selectedRecurringOccurrences().filter((occurrence) => occurrence.include);
      const selectedRows = preview.occurrences.filter((occurrence) => selected.some((item) => item.sequence === occurrence.sequence));
      const rows = selectedRows.map((occurrence) => {
        const totalCents = occurrence.totalCents ?? occurrence.pricing?.totalCents ?? occurrence.amountCents + occurrence.taxCents;
        const date = new Intl.DateTimeFormat(this.getAttribute("locale") ?? "es-ES", { dateStyle: "medium", timeStyle: "short", timeZone: preview.timezone }).format(new Date(occurrence.startAt));
        return `<li><span>${escapeHtml(date)}</span><strong>${this.renderPriceTooltip(totalCents, occurrence.currency, occurrence.pricing ?? null, `Ocurrencia ${occurrence.sequence + 1}`)}</strong></li>`;
      }).join("");
      content.innerHTML = `<div class="review-card"><h4>Revisión de tu serie</h4><p class="review-note">Se crearán ${selectedRows.length} ocurrencias después de validar disponibilidad, precio y cupón en el servidor.</p><ul class="review-lines">${rows || "<li>Elegí al menos una ocurrencia.</li>"}</ul><div class="review-total"><span>Total de la serie</span><strong>${this.recurringPricingLoading ? '<span class="spinner" aria-hidden="true"></span> Recalculando…' : this.renderPriceTooltip(preview.totalCents, preview.currency, preview.pricing ?? null, "Total de la serie")}</strong></div><label class="review-coupon">Cupón (opcional)<input name="couponCode" data-review-coupon autocomplete="off" placeholder="Código de cupón" value="${escapeHtml(previousCoupon)}" /></label><p class="review-note">El cupón cuenta una sola vez por esta serie y su descuento se distribuye en el detalle de cada ocurrencia.</p><p class="review-note">Método configurado: ${this.servicePaymentMode() === "prepay" ? "checkout alojado" : "pago en el local"}.</p></div>`;
    } else if (this.selectedSlot) {
      const quote = this.oneOffQuote ?? this.selectedSlot.quote ?? null;
      const service = this.services.find((item) => item.id === this.serviceIdFromForm());
      const date = new Intl.DateTimeFormat(this.getAttribute("locale") ?? "es-ES", { dateStyle: "full", timeStyle: "short", timeZone: this.selectedSlot.timezone ?? undefined }).format(new Date(this.selectedSlot.startAt));
      const total = quote?.totalCents ?? service?.priceCents ?? 0;
      const currency = quote?.currency ?? service?.currency ?? "EUR";
      content.innerHTML = `<div class="review-card"><h4>Revisión de tu reserva</h4><ul class="review-lines"><li><span>Servicio</span><strong>${escapeHtml(service?.name ?? "Reserva")}</strong></li><li><span>Fecha y hora</span><strong>${escapeHtml(date)}</strong></li><li><span>Profesional / sede</span><strong>${escapeHtml(this.selectedSlot.providerName ?? "Cualquier profesional")} · ${escapeHtml(this.selectedSlot.locationName ?? "Cualquier sede")}</strong></li></ul><div class="review-total"><span>Total</span><strong>${this.oneOffQuoteLoading ? '<span class="spinner" aria-hidden="true"></span> Recalculando…' : this.renderPriceTooltip(total, currency, quote, "Total de la reserva")}</strong></div><label class="review-coupon">Cupón (opcional)<input name="couponCode" data-review-coupon autocomplete="off" placeholder="Código de cupón" value="${escapeHtml(previousCoupon)}" /></label><p class="review-note">El importe final se vuelve a validar antes de crear el hold. El checkout, si corresponde, será alojado por el proveedor de pagos.</p></div>`;
    } else {
      content.innerHTML = '<div class="slots-empty">Primero elegí un horario para revisar la reserva.</div>';
    }
    this.bindPriceTooltips(content);
  }

  private renderPayment(): void {
    if (this.recurringCheckout) {
      this.renderRecurringPayment();
      return;
    }
    const content = this.root.querySelector("[data-payment-content]") as HTMLElement | null;
    const checkout = this.bookingCheckout;
    if (!content) return;
    if (!checkout) {
      this.renderReviewPayment();
      return;
    }
    const paymentStatus = this.bookingPaymentStatus?.payment?.status ?? checkout.payment.status;
    const linkStatus = this.bookingPaymentStatus?.link.status ?? checkout.payment.status;
    const bookingStatus = this.bookingPaymentStatus?.bookingStatus ?? checkout.booking.status;
    const checkoutUrl = checkout.payment.checkoutUrl;
    const checkoutQuote = checkout.quote ?? this.oneOffQuote;
    const result = renderBookingPaymentMarkup({
      paymentStatus,
      linkStatus,
      bookingStatus,
      paymentRefreshing: this.paymentRefreshing,
      checkoutUrl,
      priceTooltip: this.renderPriceTooltip(checkout.payment.amountCents, checkout.payment.currency, checkoutQuote, "Total de la reserva")
    });
    content.innerHTML = result.html;
    content.querySelector('[data-action="payment-refresh"]')?.addEventListener("click", () => void this.refreshBookingPayment(true));
  }

  private startRecurringPaymentPolling(): void {
    if (!this.recurringCheckout?.series.id || !this.client) return;
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollAttempts = 0;
    this.paymentPollTimer = window.setInterval(() => void this.refreshRecurringPayment(), 5_000);
    void this.refreshRecurringPayment();
  }

  private async refreshRecurringPayment(manual = false): Promise<void> {
    const seriesId = this.recurringCheckout?.series.id;
    if (!seriesId || !this.client) {
      this.setStatus("No pudimos identificar la serie de pago. Volvé a iniciar la reserva.", true);
      return;
    }
    if (this.paymentRefreshing) return;
    if (this.paymentPollAttempts >= 24) {
      if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
      this.paymentPollTimer = undefined;
      if (!manual) return;
      // A manual click starts a fresh bounded polling window after the
      // background poller has stopped.
      this.paymentPollAttempts = 0;
    }
    this.paymentRefreshing = true;
    this.setStatus("Consultando el estado del pago en Kuroo…", false, true);
    this.updateStepUI();
    this.paymentPollAttempts += 1;
    try {
      const series = await this.client.recurringSeriesDetail(seriesId);
      this.recurringCheckout = { ...this.recurringCheckout!, series };
      if (series.paymentState === "paid") {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
        this.currentStep = "confirmation";
        this.emitBookingConfirmed("recurring");
        this.setStatus("Pago aprobado. Todas las citas de la serie quedaron confirmadas.");
      } else if (series.paymentState === "failed") {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
        this.emitBookingCancelled("payment_failed");
        this.setStatus("El pago no fue aprobado. La serie quedó cancelada.", true);
      } else {
        this.setStatus("Mercado Pago todavía no confirmó el pago. Podés volver a intentar en unos segundos.");
      }
    } catch (error) {
      if (!isAbortError(error)) this.setStatus("No pudimos consultar el pago ahora. Podés volver a intentar.", true);
    } finally {
      this.paymentRefreshing = false;
      this.updateStepUI();
    }
  }

  private startBookingPaymentPolling(): void {
    if (!this.bookingCheckout?.paymentLink?.token || !this.client) return;
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollAttempts = 0;
    this.paymentPollTimer = window.setInterval(() => void this.refreshBookingPayment(), 5_000);
    void this.refreshBookingPayment();
  }

  private async refreshBookingPayment(manual = false): Promise<void> {
    const token = this.bookingCheckout?.paymentLink?.token;
    if (!token || !this.client) {
      this.setStatus("No pudimos identificar el enlace de pago. Volvé a iniciar la reserva.", true);
      return;
    }
    if (this.paymentRefreshing) return;
    if (this.paymentPollAttempts >= 24) {
      if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
      this.paymentPollTimer = undefined;
      if (!manual) return;
      this.paymentPollAttempts = 0;
    }
    this.paymentRefreshing = true;
    this.setStatus("Consultando el estado del pago en Kuroo…", false, true);
    this.updateStepUI();
    this.paymentPollAttempts += 1;
    try {
      const status = await this.client.paymentLinkStatus(token);
      this.bookingPaymentStatus = status;
      const paymentStatus = status.payment?.status ?? this.bookingCheckout?.payment.status;
      const bookingStatus = status.bookingStatus ?? this.bookingCheckout?.booking.status;
      if (paymentStatus === "paid" && bookingStatus === "confirmed") {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
        const booking = this.bookingCheckout!.booking;
        this.lastBooking = { ...booking, status: "confirmed" };
        this.currentStep = "confirmation";
        this.emitBookingConfirmed("single");
        this.setStatus("Pago aprobado. Tu reserva quedó confirmada.");
        this.dispatchEvent(new CustomEvent("kuroo:booking-created", { bubbles: true, composed: true, detail: this.lastBooking }));
        await this.loadSlots({ preserveStatus: true });
        this.currentStep = "confirmation";
      } else if (bookingStatus === "cancelled" || ["failed", "cancelled", "expired"].includes(paymentStatus ?? "") || ["cancelled", "expired"].includes(status.link.status)) {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
        this.emitBookingCancelled(paymentStatus === "expired" || ["cancelled", "expired"].includes(status.link.status) ? "payment_expired" : "payment_failed");
        this.setStatus(paymentStatus === "paid" ? "El pago llegó tarde y la reserva fue cancelada. Administración gestionará la devolución." : "El pago no fue aprobado. La reserva quedó cancelada.", true);
      } else {
        this.setStatus("Mercado Pago todavía no confirmó el pago. Podés volver a intentar en unos segundos.");
      }
    } catch (error) {
      if (!isAbortError(error)) this.setStatus("No pudimos consultar el pago ahora. Podés volver a intentar.", true);
    } finally {
      this.paymentRefreshing = false;
      this.updateStepUI();
    }
  }

  private async submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const form = this.root.querySelector("form") as HTMLFormElement; const submit = form.querySelector('[data-action="continue"]') as HTMLButtonElement; const customer = form.querySelector("[data-customer]") as HTMLElement;
    if (this.currentStep === "confirmation") return;
    if (this.currentStep === "payment") {
      if (this.bookingCheckout) await this.refreshBookingPayment(true);
      else if (this.recurringCheckout) await this.refreshRecurringPayment(true);
      else await this.submitReview(form, submit);
      return;
    }
    if (!this.serviceIdFromForm()) { this.setStatus("Elegí un servicio para continuar.", true); return; }
    if (!this.selectedSlot) { this.setStatus("Elegí un horario disponible.", true); return; }
    const recurrenceToggle = form.elements.namedItem("recurringEnabled") as HTMLInputElement | null;
    if (this.currentStep === "datetime" && recurrenceToggle?.checked) {
      this.applyWeeklyAnchorDefault(form);
      this.currentStep = "recurrence";
      this.updateStepUI();
      this.setStatus("Verificando disponibilidad y precio de las cuatro ocurrencias…", false, true);
      void this.refreshRecurringPreviewForSelection(form);
      return;
    }
    if (this.currentStep === "recurrence") {
      if (!this.serviceRecurrence()?.enabled) { this.setStatus("La recurrencia no está habilitada para este servicio.", true); return; }
      if (!this.recurringPreview) {
        await this.refreshRecurringPreviewForSelection(form);
        return;
      }
      if (!this.validateRecurringOccurrenceInputs()) return;
      this.currentStep = "information";
      this.updateStepUI();
      this.setStatus("Revisá las fechas y completá tus datos para continuar a Revisión y pago.");
      return;
    }
    if (this.currentStep === "datetime") {
      this.currentStep = "information";
      this.updateStepUI();
      this.setStatus("Completá tus datos para revisar el precio y confirmar la reserva.");
      if (!this.oneOffQuote) void this.refreshOneOffQuote(form);
      return;
    }
    if (this.currentStep === "information" && !this.pendingHold) {
      const values = Object.fromEntries(new FormData(form).entries());
      const email = String(values.email ?? "").trim();
      const firstName = String(values.firstName ?? "").trim();
      const consent = values.consent === "on";
      if (!email || !firstName || !consent) { this.setStatus("Completá nombre, email y consentimiento para continuar.", true); return; }
      this.emitClientSelected();
      if (this.recurringPreview && !this.validateRecurringOccurrenceInputs()) return;
      this.currentStep = "payment";
      this.updateStepUI();
      this.setStatus("Revisá el detalle final. Todavía no se creó ningún hold ni checkout.");
      if (!this.recurringPreview && !this.oneOffQuote) void this.refreshOneOffQuote(form);
      return;
    }
    if (!this.pendingHold) {
      if (this.currentStep === "information" && this.recurringPreview) {
        if (!this.validateRecurringOccurrenceInputs()) return;
        const values = Object.fromEntries(new FormData(form).entries()); const email = String(values.email ?? "").trim(); const firstName = String(values.firstName ?? "").trim(); const consent = values.consent === "on";
        if (!email || !firstName || !consent) { this.setStatus("Completá nombre, email y consentimiento para crear la serie.", true); return; }
        this.emitClientSelected();
        submit.disabled = true; this.setStatus("Creando la serie y reservando temporalmente todas las fechas…", false, true);
        try {
          const slot = this.selectedSlot;
          const rule = this.recurringRuleFromForm(form);
          const checkout = await this.client!.checkoutRecurringSeries({
            serviceId: this.serviceIdFromForm(), providerId: slot.providerId ?? null, locationId: slot.locationId ?? null,
             timezone: slot.timezone ?? "UTC", startsAt: slot.startAt, count: this.recurringPreview.occurrences.length,
            rule, persons: Math.max(1, Number(values.persons ?? 1) || 1), participants: this.recurringParticipantsFromForm(form), extras: this.selectedExtrasFromForm(form, true), couponCode: String(values.couponCode ?? "").trim() || undefined,
            occurrences: this.selectedRecurringOccurrences(), customer: { email, firstName, lastName: String(values.lastName ?? "").trim() || undefined, phone: String(values.phone ?? "").trim() || undefined, consent: true },
            notes: String(values.notes ?? "").trim() || undefined, paymentMethod: "mercadopago", idempotencyKey: randomIdempotency("public-recurring-checkout")
          });
          this.recurringCheckout = checkout;
          this.dispatchEvent(new CustomEvent("kuroo:recurring-series-created", { bubbles: true, composed: true, detail: checkout }));
          if (checkout.payment) {
            this.currentStep = "payment";
            this.startRecurringPaymentPolling();
            this.setStatus("Serie creada. Completá el pago único en Mercado Pago.");
          } else {
            this.lastBooking = checkout.bookings[0];
            this.currentStep = "confirmation";
            this.emitBookingConfirmed("recurring");
            this.setStatus(`Serie confirmada con ${checkout.bookings.length} ocurrencia${checkout.bookings.length === 1 ? "" : "s"}.`);
          }
          submit.disabled = false;
          this.updateStepUI();
        } catch (error) {
          submit.disabled = false;
          this.emitSlotConflict(error);
          this.setStatus(this.errorMessage(error), true);
          this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "recurring-checkout", error } }));
        }
        return;
      }
      submit.disabled = true; this.setStatus("Reservando temporalmente ese horario…", false, true);
      try {
        const slot = this.selectedSlot; const result = await this.client!.holdSlot({ slotKey: slot.slotKey, startAt: slot.startAt, endAt: slot.endAt, serviceId: this.serviceIdFromForm(), providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined, capacity: Number((form.elements.namedItem("persons") as HTMLInputElement)?.value || 1), idempotencyKey: randomIdempotency("public-hold") });
        this.pendingHold = { ...result, startAt: slot.startAt, endAt: slot.endAt, providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined };
        customer.hidden = false;
        this.currentStep = "information";
        submit.textContent = "Confirmar reserva"; submit.disabled = false;
        this.startCountdown(result.expiresAt, form); this.updateStepUI(); this.setStatus("Completá tus datos y confirmá antes de que expire el hold."); this.dispatchEvent(new CustomEvent("kuroo:slot-held", { bubbles: true, composed: true, detail: result }));
      } catch (error) { submit.disabled = false; this.emitSlotConflict(error); this.setStatus(this.errorMessage(error), true); this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "hold", error } })); }
      return;
    }
    const values = Object.fromEntries(new FormData(form).entries()); const email = String(values.email ?? "").trim(); const firstName = String(values.firstName ?? "").trim(); const consent = values.consent === "on";
    if (!email || !firstName || !consent) { this.setStatus("Completá nombre, email y consentimiento para confirmar.", true); return; }
    this.emitClientSelected();
    submit.disabled = true; this.setStatus("Confirmando tu reserva…", false, true);
    try {
      const result = await this.client!.createBooking({ holdId: this.pendingHold.holdId, holdCapability: this.pendingHold.holdCapability, slotKey: this.pendingHold.slotKey, serviceId: this.serviceIdFromForm(), providerId: this.pendingHold.providerId, locationId: this.pendingHold.locationId, startAt: this.pendingHold.startAt, endAt: this.pendingHold.endAt, persons: Math.max(1, Number(values.persons ?? 1) || 1), quoteId: this.pendingHold.quote?.id ?? undefined, idempotencyKey: randomIdempotency("public-booking"), notes: String(values.notes ?? "").trim() || undefined, extras: this.selectedExtrasFromForm(form), couponCode: this.couponCodeFromForm(form), paymentMethod: "mercadopago", customer: { email, firstName, lastName: String(values.lastName ?? "").trim() || undefined, phone: String(values.phone ?? "").trim() || undefined, consent: true } });
      this.pendingHold = undefined; if (this.countdownTimer) window.clearInterval(this.countdownTimer); const banner = form.querySelector("[data-hold]") as HTMLElement; banner.hidden = true;
      if ("booking" in result) {
        this.bookingCheckout = result;
        this.bookingPaymentStatus = undefined;
        this.currentStep = "payment";
        this.startBookingPaymentPolling();
        this.setStatus("Reserva creada. Completá el pago único en Mercado Pago.");
        this.dispatchEvent(new CustomEvent("kuroo:booking-checkout-created", { bubbles: true, composed: true, detail: result }));
      } else {
        this.lastBooking = result;
        this.currentStep = "confirmation"; this.updateStepUI(); submit.textContent = "Reserva confirmada";
        const confirmation = `Reserva confirmada. Código ${result.id.slice(0, 8)}.`; this.emitBookingConfirmed("single"); this.setStatus(confirmation); this.dispatchEvent(new CustomEvent("kuroo:booking-created", { bubbles: true, composed: true, detail: result })); await this.loadSlots({ preserveStatus: true }); this.currentStep = "confirmation"; this.updateStepUI(); submit.textContent = "Reserva confirmada"; this.setStatus(confirmation);
      }
      this.updateStepUI();
    } catch (error) { submit.disabled = false; this.emitSlotConflict(error); this.setStatus(this.errorMessage(error), true); this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "booking", error } })); }
  }

  private async submitReview(form: HTMLFormElement, submit: HTMLButtonElement): Promise<void> {
    const slot = this.selectedSlot;
    if (!slot || !this.client) return;
    const values = Object.fromEntries(new FormData(form).entries());
    const email = String(values.email ?? "").trim();
    const firstName = String(values.firstName ?? "").trim();
    const consent = values.consent === "on";
    if (!email || !firstName || !consent) { this.setStatus("Completá nombre, email y consentimiento para confirmar.", true); return; }
    if (this.recurringPreview) {
      if (!this.validateRecurringOccurrenceInputs()) return;
      const selections = this.selectedRecurringOccurrences();
      if (!selections.some((occurrence) => occurrence.include)) { this.setStatus("Conservá al menos una ocurrencia para continuar.", true); return; }
      if (this.recurringPreview.occurrences.some((occurrence) => occurrence.status === "unavailable" && selections.some((selection) => selection.sequence === occurrence.sequence && selection.include))) { this.setStatus("Hay ocurrencias sin disponibilidad. Quitalas o modificá sus datos antes de continuar.", true); return; }
      submit.disabled = true;
      this.setStatus("Validando nuevamente disponibilidad, precio y cupón antes de crear la serie…", false, true);
      try {
        const checkout = await this.client.checkoutRecurringSeries({
          serviceId: this.serviceIdFromForm(), providerId: slot.providerId ?? null, locationId: slot.locationId ?? null,
          timezone: slot.timezone ?? "UTC", startsAt: slot.startAt, count: this.recurringCountFromForm(form, this.recurringPreview.occurrences.length),
          rule: this.recurringRuleFromForm(form), persons: Math.max(1, Number(values.persons ?? 1) || 1), participants: this.recurringParticipantsFromForm(form),
          extras: this.selectedExtrasFromForm(form, true), couponCode: this.couponCodeFromForm(form), occurrences: selections,
          customer: { email, firstName, lastName: String(values.lastName ?? "").trim() || undefined, phone: String(values.phone ?? "").trim() || undefined, consent: true },
          notes: String(values.notes ?? "").trim() || undefined, paymentMethod: "mercadopago", idempotencyKey: randomIdempotency("public-recurring-checkout")
        });
        this.recurringCheckout = checkout;
        this.dispatchEvent(new CustomEvent("kuroo:recurring-series-created", { bubbles: true, composed: true, detail: checkout }));
        if (checkout.payment) {
          this.currentStep = "payment";
          this.startRecurringPaymentPolling();
          this.setStatus("Serie creada. Completá el checkout alojado para confirmar todas las fechas.");
        } else {
          this.lastBooking = checkout.bookings[0];
        this.currentStep = "confirmation";
        this.emitBookingConfirmed("recurring");
        this.setStatus(`Serie confirmada con ${checkout.bookings.length} ocurrencia${checkout.bookings.length === 1 ? "" : "s"}.`);
        }
      } catch (error) {
        this.emitSlotConflict(error);
        if (error instanceof KurooApiError && error.code === "QUOTE_CHANGED") {
          this.currentStep = "recurrence";
          this.setStatus("El precio cambió mientras confirmábamos. Actualizamos la cotización; revisala y confirmá otra vez.", true);
          await this.refreshRecurringPreviewForSelection(form);
        } else {
          this.setStatus(this.errorMessage(error), true);
        }
        this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "recurring-checkout", error } }));
      } finally {
        submit.disabled = false;
        this.updateStepUI();
      }
      return;
    }

    submit.disabled = true;
    this.setStatus("Verificando disponibilidad, precio y cupón antes de reservar…", false, true);
    try {
      const service = this.services.find((item) => item.id === this.serviceIdFromForm());
      if (!service) throw new Error("SERVICE_NOT_FOUND");
      const persons = Math.max(1, Number(values.persons ?? 1) || 1);
      const endAt = this.oneOffEndAt(form, slot, service);
      const quote = await this.client.quoteService({
        serviceId: service.id, providerId: slot.providerId ?? null, locationId: slot.locationId ?? null,
        startAt: slot.startAt, endAt, persons, extras: this.selectedExtrasFromForm(form), couponCode: this.couponCodeFromForm(form), slotKey: slot.slotKey,
        timezone: slot.timezone ?? "UTC", idempotencyKey: randomIdempotency("public-final-quote")
      });
      this.oneOffQuote = quote;
      const result = await this.client.holdSlot({
        slotKey: slot.slotKey, startAt: slot.startAt, endAt, serviceId: service.id, providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined,
        persons, capacity: persons, extras: this.selectedExtrasFromForm(form), quoteId: quote.id, idempotencyKey: randomIdempotency("public-hold")
      });
      this.pendingHold = { ...result, startAt: slot.startAt, endAt, providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined };
      const bookingResult = await this.client.createBooking({
        holdId: result.holdId, holdCapability: result.holdCapability, slotKey: slot.slotKey, serviceId: service.id, providerId: slot.providerId ?? undefined, locationId: slot.locationId ?? undefined,
        startAt: slot.startAt, endAt, persons, quoteId: quote.id, idempotencyKey: randomIdempotency("public-booking"), notes: String(values.notes ?? "").trim() || undefined,
        extras: this.selectedExtrasFromForm(form), couponCode: this.couponCodeFromForm(form), paymentMethod: "mercadopago",
        customer: { email, firstName, lastName: String(values.lastName ?? "").trim() || undefined, phone: String(values.phone ?? "").trim() || undefined, consent: true }
      });
      this.pendingHold = undefined;
      if ("booking" in bookingResult) {
        this.bookingCheckout = bookingResult;
        this.bookingPaymentStatus = undefined;
        this.currentStep = "payment";
        this.startBookingPaymentPolling();
        this.setStatus("Reserva creada. Completá el checkout alojado para confirmarla.");
        this.dispatchEvent(new CustomEvent("kuroo:booking-checkout-created", { bubbles: true, composed: true, detail: bookingResult }));
      } else {
        this.lastBooking = bookingResult;
        this.currentStep = "confirmation";
        this.emitBookingConfirmed("single");
        this.setStatus(`Reserva confirmada. Código ${bookingResult.id.slice(0, 8)}.`);
        this.dispatchEvent(new CustomEvent("kuroo:booking-created", { bubbles: true, composed: true, detail: bookingResult }));
      }
    } catch (error) {
      await this.releasePendingHold();
      this.emitSlotConflict(error);
      this.setStatus(this.errorMessage(error), true);
      this.dispatchEvent(new CustomEvent("kuroo:booking-error", { bubbles: true, composed: true, detail: { stage: "review", error } }));
    } finally {
      submit.disabled = false;
      this.updateStepUI();
    }
  }

  private applyWeeklyAnchorDefault(form: HTMLFormElement): void {
    if (this.weeklyDaysCustomized || !this.selectedSlot) return;
    const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: this.selectedSlot.timezone ?? "UTC" }).format(new Date(this.selectedSlot.startAt));
    const dayByName: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const selectedDay = dayByName[weekday];
    if (selectedDay === undefined) return;
    form.querySelectorAll<HTMLInputElement>('input[name="weeklyDay"]').forEach((input) => { input.checked = Number(input.value) === selectedDay; });
  }
}

export function defineKurooWidgets(): void {
  if (!customElements.get(elementName)) customElements.define(elementName, KurooBookingElement);
}

defineKurooWidgets();
